// AdminAttendanceManagement.js - UPDATED TO FETCH ALL RECORDS FROM DATABASE
import React, { useState, useEffect } from 'react';
import * as XLSX from 'xlsx';
import { API_LINK } from '../../apiLink/apiLink';
import Layout from '../../components/Layout';

const AdminAttendanceManagement = ({ onNavigate, userInfo = {} }) => {
  // =============================================
  // STATE MANAGEMENT
  // =============================================

  // UI State
  const [isMobile, setIsMobile] = useState(false);
  const [attendanceType, setAttendanceType] = useState('regular'); // 'regular' or 'training'

  // Data State - CLIENT-SIDE FILTERING APPROACH
  const [allRecords, setAllRecords] = useState([]); // Store ALL records from database
  const [allRecordsCount, setAllRecordsCount] = useState(0); const [filteredRecords, setFilteredRecords] = useState([]); // Store filtered results
  const [displayedRecords, setDisplayedRecords] = useState([]); // Store paginated results
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [initialLoad, setInitialLoad] = useState(true);

  // Training Data State
  const [allTrainingRecords, setAllTrainingRecords] = useState([]);
  const [filteredTrainingRecords, setFilteredTrainingRecords] = useState([]);
  const [displayedTrainingRecords, setDisplayedTrainingRecords] = useState([]);
  const [trainingLoading, setTrainingLoading] = useState(false);
  const [trainingError, setTrainingError] = useState(null);
  const [initialTrainingLoad, setInitialTrainingLoad] = useState(true);

  // Statistics State
  const [statistics, setStatistics] = useState({
    totalPresent: 0,
    totalAbsent: 0,
    totalRecords: 0,
    attendancePercentage: 0
  });

  // Training Statistics State
  const [trainingStatistics, setTrainingStatistics] = useState({
    totalPresent: 0,
    totalAbsent: 0,
    totalRecords: 0,
    attendancePercentage: 0
  });

  // Today's Training Stats
  const [todayTrainingStats, setTodayTrainingStats] = useState({
    totalEnrolledStudents: 0,
    studentsPresent: 0,
    percentagePresent: 0
  });

  // DYNAMIC DEPARTMENT DATA
  const [departments, setDepartments] = useState([]);
  const [departmentsLoading, setDepartmentsLoading] = useState(false);

  // Training Filter Options
  const [trainingPrograms, setTrainingPrograms] = useState([]);
  const [trainingBatches, setTrainingBatches] = useState([]);
  const [trainingSubjects, setTrainingSubjects] = useState([]);

  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [limit] = useState(25); // Increased page size for better UX with large datasets

  // Filter State - Regular Attendance
  const [filterDepartment, setFilterDepartment] = useState('');
  const [filterDate, setFilterDate] = useState('');
  const [filterStudentId, setFilterStudentId] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterSubject, setFilterSubject] = useState('');
  const [filterBatchName, setFilterBatchName] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  // Training Filter State
  const [filterTrainingProgram, setFilterTrainingProgram] = useState('');
  const [filterTrainingBatch, setFilterTrainingBatch] = useState('');
  const [filterTrainingDate, setFilterTrainingDate] = useState('');
  const [filterTrainingStudentId, setFilterTrainingStudentId] = useState('');
  const [filterTrainingStatus, setFilterTrainingStatus] = useState('');
  const [filterTrainingSubject, setFilterTrainingSubject] = useState('');
  const [trainingSearchTerm, setTrainingSearchTerm] = useState('');

  // Modal State
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);

  // Form State
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    department: '',
    year: '',
    batch_name: '',
    student_id: '',
    status: 'present',
    subject: ''
  });

  // Training Form State
  const [trainingFormData, setTrainingFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    training_program_id: '',
    training_batch_id: '',
    student_id: '',
    status: 'present',
    subject_code: '',
    session_hours: 1.0,
    remarks: ''
  });

  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  // Student Lookup State
  const [resolvedStudent, setResolvedStudent] = useState(null);
  const [resolvedStudentLoading, setResolvedStudentLoading] = useState(false);

  // Fetch student details by student ID dynamically
  useEffect(() => {
    const studentId = attendanceType === 'regular' ? formData.student_id : trainingFormData.student_id;
    
    // Clear resolved student if ID is short or empty
    if (!studentId || studentId.trim().length < 3) {
      setResolvedStudent(null);
      return;
    }

    const delayDebounceFn = setTimeout(async () => {
      setResolvedStudentLoading(true);
      try {
        const response = await apiCall(`/students?search=${encodeURIComponent(studentId.trim())}`);
        if (response.success && response.data && response.data.students) {
          const searchVal = studentId.trim().toLowerCase();
          // Try exact match first
          let student = response.data.students.find(s => s.student_id.toLowerCase() === searchVal);
          
          // If no exact match on student_id, fallback to first student whose student_id contains searchVal
          if (!student) {
            student = response.data.students.find(s => s.student_id.toLowerCase().includes(searchVal));
          }

          if (student) {
            setResolvedStudent(student);
            
            // Auto-fill other fields if it's a NEW record (selectedRecord is null)
            if (!selectedRecord) {
              if (attendanceType === 'regular') {
                let yearString = student.year ? String(student.year) : '';
                setFormData(prev => ({
                  ...prev,
                  department: student.dept || prev.department,
                  year: yearString || prev.year,
                  batch_name: student.batch_name || prev.batch_name
                }));
              }
            }
          } else {
            setResolvedStudent(null);
          }
        } else {
          setResolvedStudent(null);
        }
      } catch (error) {
        console.error('Error looking up student:', error);
        setResolvedStudent(null);
      } finally {
        setResolvedStudentLoading(false);
      }
    }, 500); // 500ms debounce

    return () => clearTimeout(delayDebounceFn);
  }, [formData.student_id, trainingFormData.student_id, attendanceType, selectedRecord]);

  /* OLD */
  // Attendance Analytics State
  const [analyticsData, setAnalyticsData] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [showAnalytics, setShowAnalytics] = useState(false);

  // Subject-Wise Analytics State
  const [subjectsList, setSubjectsList] = useState([]);
  const [subjectsListLoading, setSubjectsListLoading] = useState(false);
  const [analyticsFilterDept, setAnalyticsFilterDept] = useState('');
  const [analyticsFilterSubject, setAnalyticsFilterSubject] = useState('');
  const [subjectWiseData, setSubjectWiseData] = useState(null);
  const [subjectWiseLoading, setSubjectWiseLoading] = useState(false);
  const [showSubjectWiseAnalytics, setShowSubjectWiseAnalytics] = useState(false);

  // NEW: Regular subjects list for analytics
  const [regularSubjectsList, setRegularSubjectsList] = useState([]);
  const [regularSubjectsLoading, setRegularSubjectsLoading] = useState(false);

  // NEW: Expanded category data with students
  const [expandedCategory, setExpandedCategory] = useState(null);

  // NEW: Subject-Wise Analytics with hierarchical filters (Department → Year → Batch → Subject)
  const [subjectAnalyticsDept, setSubjectAnalyticsDept] = useState('');
  const [subjectAnalyticsYear, setSubjectAnalyticsYear] = useState('');
  const [subjectAnalyticsBatch, setSubjectAnalyticsBatch] = useState('');
  const [subjectAnalyticsSubject, setSubjectAnalyticsSubject] = useState('');
  const [subjectAnalyticsBatches, setSubjectAnalyticsBatches] = useState([]);
  const [subjectAnalyticsSubjects, setSubjectAnalyticsSubjects] = useState([]);
  const [subjectAnalyticsData, setSubjectAnalyticsData] = useState(null);
  const [subjectAnalyticsLoading, setSubjectAnalyticsLoading] = useState(false);

  // NEW: Date filters for subject analytics
  const [subjectAnalyticsFromDate, setSubjectAnalyticsFromDate] = useState('');
  const [subjectAnalyticsToDate, setSubjectAnalyticsToDate] = useState('');

  // =============================================
  // UTILITY FUNCTIONS
  // =============================================



  const formatDate = (dateString) => {
    if (!dateString || dateString === '0000-00-00') return 'Not set';
    try {
      // If it's in YYYY-MM-DD format, format directly to DD/MM/YYYY to avoid timezone shift
      if (typeof dateString === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
        const [year, month, day] = dateString.split('-');
        return `${day}/${month}/${year}`;
      }

      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString;
      
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      
      return `${day}/${month}/${year}`;
    } catch (e) {
      return dateString;
    }
  };

  // FIXED: Normalize date function to ensure consistent date comparison WITHOUT timezone issues
  const normalizeDate = (dateString) => {
    if (!dateString) return '';
    try {
      // If it's already in YYYY-MM-DD format, return as-is
      if (typeof dateString === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
        return dateString;
      }

      // Handle other date formats - create date in local timezone to avoid UTC conversion issues
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '';

      // Use local date methods to avoid timezone shifts
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');

      return `${year}-${month}-${day}`;
    } catch (e) {
      console.error('Date normalization error:', e, 'Input:', dateString);
      return '';
    }
  };

  // FIXED: Safe string getter with null checks
  const safeString = (value) => {
    if (value === null || value === undefined) return '';
    return String(value);
  };

  // FIXED: Safe toLowerCase with null checks
  const safeToLowerCase = (value) => {
    return safeString(value).toLowerCase();
  };

  // =============================================
  // API HELPER FUNCTION
  // =============================================

  const apiCall = async (endpoint, options = {}) => {
    const token = localStorage.getItem('token') || sessionStorage.getItem('token');

    if (!token) {
      throw new Error('Authentication token not found');
    }

    const response = await fetch(`${API_LINK}/api/admin${endpoint}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        ...options.headers
      },
      ...options
    });

    if (!response.ok) {
      if (response.status === 401) {
        localStorage.removeItem('token');
        sessionStorage.removeItem('token');
        throw new Error('Session expired');
      }
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.json();
  };

  // =============================================
  // STATISTICS CALCULATION
  // =============================================

  const calculateStatistics = (records) => {
    const totalRecords = records.length;
    const totalPresent = records.filter(record => safeToLowerCase(record.status) === 'present').length;
    const totalAbsent = records.filter(record => safeToLowerCase(record.status) === 'absent').length;
    const attendancePercentage = totalRecords > 0 ? Math.round((totalPresent / totalRecords) * 100) : 0;

    return {
      totalPresent,
      totalAbsent,
      totalRecords,
      attendancePercentage
    };
  };

  // ============================================= 
  // FIXED CLIENT-SIDE FILTERING FUNCTIONS
  // =============================================

  // FIXED: Improved client-side filtering function for REGULAR attendance
  const applyFilters = (records, searchText, department, date, studentId, status, subject, batchName) => {
    console.log('🔍 APPLYING REGULAR FILTERS:', {
      totalRecords: records.length,
      filters: { searchText, department, date, studentId, status, subject, batchName }
    });

    let filtered = [...records];

    // Apply search filter (case-insensitive across multiple fields)
    if (searchText && searchText.trim()) {
      const searchLower = searchText.trim().toLowerCase();
      filtered = filtered.filter(record => {
        const studentIdMatch = safeToLowerCase(record.student_id).includes(searchLower);
        const subjectMatch = safeToLowerCase(record.subject).includes(searchLower);
        const batchMatch = safeToLowerCase(record.batch_name).includes(searchLower);
        const deptMatch = safeToLowerCase(record.department).includes(searchLower);

        return studentIdMatch || subjectMatch || batchMatch || deptMatch;
      });
      console.log(`🔍 After search filter (${searchText}): ${filtered.length} records`);
    }

    // Apply department filter
    if (department && department.trim()) {
      filtered = filtered.filter(record => {
        const recordDept = safeString(record.department);
        return recordDept === department;
      });
      console.log(`🏢 After department filter (${department}): ${filtered.length} records`);
    }

    // FIXED: Apply date filter with proper date normalization and detailed logging
    if (date && date.trim()) {
      const targetDate = normalizeDate(date);
      console.log(`📅 FILTERING BY DATE: Selected="${date}" -> Normalized="${targetDate}"`);

      filtered = filtered.filter(record => {
        const recordDate = normalizeDate(record.date);
        const matches = recordDate === targetDate;

        if (recordDate && !matches) {
          console.log(`📅 DATE MISMATCH: Record date="${record.date}" -> Normalized="${recordDate}" vs Filter="${targetDate}"`);
        } else if (matches) {
          console.log(`📅 DATE MATCH: Record date="${record.date}" -> Normalized="${recordDate}" ✅`);
        }

        return matches;
      });
      console.log(`📅 After date filter (${date} -> ${targetDate}): ${filtered.length} records`);
    }

    // Apply student ID filter
    if (studentId && studentId.trim()) {
      const studentIdLower = studentId.trim().toLowerCase();
      filtered = filtered.filter(record => {
        const recordStudentId = safeToLowerCase(record.student_id);
        return recordStudentId.includes(studentIdLower);
      });
      console.log(`👤 After student ID filter (${studentId}): ${filtered.length} records`);
    }

    // Apply status filter
    if (status && status.trim()) {
      filtered = filtered.filter(record => {
        const recordStatus = safeToLowerCase(record.status);
        return recordStatus === status.toLowerCase();
      });
      console.log(`✅ After status filter (${status}): ${filtered.length} records`);
    }

    // Apply subject filter
    if (subject && subject.trim()) {
      const subjectLower = subject.trim().toLowerCase();
      filtered = filtered.filter(record => {
        const recordSubject = safeToLowerCase(record.subject);
        return recordSubject.includes(subjectLower);
      });
      console.log(`📚 After subject filter (${subject}): ${filtered.length} records`);
    }

    // Apply batch name filter
    if (batchName && batchName.trim()) {
      const batchLower = batchName.trim().toLowerCase();
      filtered = filtered.filter(record => {
        const recordBatch = safeToLowerCase(record.batch_name);
        return recordBatch.includes(batchLower);
      });
      console.log(`🎓 After batch filter (${batchName}): ${filtered.length} records`);
    }

    console.log(`✅ FINAL REGULAR FILTER RESULT: ${filtered.length} records from ${records.length} total`);

    // Update filtered records
    setFilteredRecords(filtered);
    setTotalCount(filtered.length);

    // Calculate statistics from filtered data
    const stats = calculateStatistics(filtered);
    setStatistics(stats);

    // Calculate pagination
    const totalPagesCalc = Math.ceil(filtered.length / limit);
    setTotalPages(totalPagesCalc);

    // Reset to first page when filters change
    setCurrentPage(1);

    // Apply pagination to filtered results
    applyPagination(filtered, 1);
  };

  // FIXED: Improved client-side filtering function for TRAINING attendance
  const applyTrainingFilters = (records, searchText, program, batch, date, studentId, status, subject) => {
    console.log('🔍 APPLYING TRAINING FILTERS:', {
      totalRecords: records.length,
      filters: { searchText, program, batch, date, studentId, status, subject }
    });

    let filtered = [...records];

    // Apply search filter
    if (searchText && searchText.trim()) {
      const searchLower = searchText.trim().toLowerCase();
      filtered = filtered.filter(record => {
        const studentIdMatch = safeToLowerCase(record.student_id).includes(searchLower);
        const studentNameMatch = safeToLowerCase(record.student_name).includes(searchLower);
        const programMatch = safeToLowerCase(record.program_name).includes(searchLower);
        const subjectMatch = safeToLowerCase(record.subject_name).includes(searchLower);
        const subjectCodeMatch = safeToLowerCase(record.subject_code).includes(searchLower);
        const instructorMatch = safeToLowerCase(record.instructor_name).includes(searchLower);

        return studentIdMatch || studentNameMatch || programMatch || subjectMatch || subjectCodeMatch || instructorMatch;
      });
      console.log(`🔍 After training search filter (${searchText}): ${filtered.length} records`);
    }

    // FIXED: Apply program filter with better matching
    if (program && program.trim()) {
      filtered = filtered.filter(record => {
        const recordProgram = safeString(record.training_program_id);
        const recordProgramName = safeToLowerCase(record.program_name);
        const programLower = program.toLowerCase();

        // Match by ID or name
        const matches = recordProgram === program || recordProgramName.includes(programLower);

        if (!matches) {
          console.log(`🏢 Program mismatch: record(id:${recordProgram}, name:"${record.program_name}") vs filter("${program}")`);
        }

        return matches;
      });
      console.log(`🏢 After training program filter (${program}): ${filtered.length} records`);
    }

    // FIXED: Apply batch filter with better matching  
    if (batch && batch.trim()) {
      filtered = filtered.filter(record => {
        const recordBatch = safeString(record.training_batch_id);
        const recordBatchName = safeToLowerCase(record.training_batch_name);
        const batchLower = batch.toLowerCase();

        // Match by ID or name
        const matches = recordBatch === batch || recordBatchName.includes(batchLower);

        if (!matches) {
          console.log(`📁 Batch mismatch: record(id:${recordBatch}, name:"${record.training_batch_name}") vs filter("${batch}")`);
        }

        return matches;
      });
      console.log(`📁 After training batch filter (${batch}): ${filtered.length} records`);
    }

    // FIXED: Apply training date filter with proper date normalization and detailed logging
    if (date && date.trim()) {
      const targetDate = normalizeDate(date);
      console.log(`📅 FILTERING TRAINING BY DATE: Selected="${date}" -> Normalized="${targetDate}"`);

      filtered = filtered.filter(record => {
        const recordDate = normalizeDate(record.date);
        const matches = recordDate === targetDate;

        if (recordDate && !matches) {
          console.log(`📅 TRAINING DATE MISMATCH: Record date="${record.date}" -> Normalized="${recordDate}" vs Filter="${targetDate}"`);
        } else if (matches) {
          console.log(`📅 TRAINING DATE MATCH: Record date="${record.date}" -> Normalized="${recordDate}" ✅`);
        }

        return matches;
      });
      console.log(`📅 After training date filter (${date} -> ${targetDate}): ${filtered.length} records`);
    }

    // Apply student ID filter
    if (studentId && studentId.trim()) {
      const studentIdLower = studentId.trim().toLowerCase();
      filtered = filtered.filter(record => {
        const recordStudentId = safeToLowerCase(record.student_id);
        return recordStudentId.includes(studentIdLower);
      });
      console.log(`👤 After training student ID filter (${studentId}): ${filtered.length} records`);
    }

    // Apply status filter
    if (status && status.trim()) {
      filtered = filtered.filter(record => {
        const recordStatus = safeToLowerCase(record.status);
        return recordStatus === status.toLowerCase();
      });
      console.log(`✅ After training status filter (${status}): ${filtered.length} records`);
    }

    // Apply subject filter
    if (subject && subject.trim()) {
      const subjectLower = subject.trim().toLowerCase();
      filtered = filtered.filter(record => {
        const recordSubjectCode = safeToLowerCase(record.subject_code);
        const recordSubjectName = safeToLowerCase(record.subject_name);
        return recordSubjectCode.includes(subjectLower) || recordSubjectName.includes(subjectLower);
      });
      console.log(`📚 After training subject filter (${subject}): ${filtered.length} records`);
    }

    console.log(`✅ FINAL TRAINING FILTER RESULT: ${filtered.length} records from ${records.length} total`);

    // Update filtered records
    setFilteredTrainingRecords(filtered);
    setTotalCount(filtered.length);

    // Calculate statistics from filtered data
    const stats = calculateStatistics(filtered);
    setTrainingStatistics(stats);

    // Calculate pagination
    const totalPagesCalc = Math.ceil(filtered.length / limit);
    setTotalPages(totalPagesCalc);

    // Reset to first page when filters change
    setCurrentPage(1);

    // Apply pagination to filtered results
    applyTrainingPagination(filtered, 1);
  };

  // Client-side pagination function
  const applyPagination = (filteredData, page) => {
    const startIndex = (page - 1) * limit;
    const endIndex = startIndex + limit;
    const paginatedData = filteredData.slice(startIndex, endIndex);

    setDisplayedRecords(paginatedData);
    setCurrentPage(page);

    console.log(`📄 PAGINATION: Showing page ${page}: ${paginatedData.length} records (${startIndex + 1}-${Math.min(endIndex, filteredData.length)} of ${filteredData.length})`);
  };

  // Training pagination function
  const applyTrainingPagination = (filteredData, page) => {
    const startIndex = (page - 1) * limit;
    const endIndex = startIndex + limit;
    const paginatedData = filteredData.slice(startIndex, endIndex);

    setDisplayedTrainingRecords(paginatedData);
    setCurrentPage(page);

    console.log(`📄 TRAINING PAGINATION: Showing page ${page}: ${paginatedData.length} records (${startIndex + 1}-${Math.min(endIndex, filteredData.length)} of ${filteredData.length})`);
  };

  // =============================================
  // FETCH FUNCTIONS - UPDATED TO GET ALL RECORDS
  // =============================================

  const fetchDepartments = async () => {
    try {
      setDepartmentsLoading(true);
      const result = await apiCall('/departments');

      if (result.success) {
        console.log('✅ Departments fetched successfully:', result.data);
        setDepartments(result.data);
      }
    } catch (error) {
      console.error('Error fetching departments:', error);
      setError('Failed to fetch departments: ' + error.message); // This will appear in UI if used
    } finally {
      setDepartmentsLoading(false);
    }
  };

  const fetchTrainingFilterOptions = async () => {
    try {
      console.log('📡 Fetching training filter options...');

      // Try to fetch training programs, batches, and subjects
      const promises = [
        apiCall('/training-programs').catch(err => {
          console.log('⚠️ Training programs endpoint failed:', err.message);
          return { success: false, data: { programs: [] } };
        }),
        apiCall('/training-batches').catch(err => {
          console.log('⚠️ Training batches endpoint failed:', err.message);
          return { success: false, data: { batches: [] } };
        }),
        apiCall('/training-subjects').catch(err => {
          console.log('⚠️ Training subjects endpoint failed:', err.message);
          return { success: false, data: { subjects: [] } };
        })
      ];

      const [programsRes, batchesRes, subjectsRes] = await Promise.all(promises);

      console.log('📊 Training filter options responses:', {
        programs: programsRes.success ? programsRes.data?.programs?.length || 0 : 'failed',
        batches: batchesRes.success ? batchesRes.data?.batches?.length || 0 : 'failed',
        subjects: subjectsRes.success ? subjectsRes.data?.subjects?.length || 0 : 'failed'
      });

      // Set training programs
      if (programsRes.success && programsRes.data?.programs) {
        setTrainingPrograms(programsRes.data.programs);
        console.log('✅ Training programs loaded:', programsRes.data.programs.length);
      } else {
        // Fallback: Create sample programs for testing
        const samplePrograms = [
          { id: '1', program_name: 'Data Analysis Training' },
          { id: '2', program_name: 'Web Development Training' },
          { id: '3', program_name: 'Digital Marketing Training' }
        ];
        setTrainingPrograms(samplePrograms);
        console.log('🔧 Using sample training programs');
      }

      // Set training batches  
      if (batchesRes.success && batchesRes.data?.batches) {
        setTrainingBatches(batchesRes.data.batches);
        console.log('✅ Training batches loaded:', batchesRes.data.batches.length);
      } else {
        // Fallback: Create sample batches for testing
        const sampleBatches = [
          { id: '101', batch_name: 'Batch A', training_program_id: '1' },
          { id: '102', batch_name: 'Batch B', training_program_id: '1' },
          { id: '201', batch_name: 'Web Dev Batch 1', training_program_id: '2' }
        ];
        setTrainingBatches(sampleBatches);
        console.log('🔧 Using sample training batches');
      }

      // Set training subjects
      if (subjectsRes.success && subjectsRes.data?.subjects) {
        setTrainingSubjects(subjectsRes.data.subjects);
        console.log('✅ Training subjects loaded:', subjectsRes.data.subjects.length);
      } else {
        // Fallback: Create sample subjects for testing
        const sampleSubjects = [
          { id: '1', subject_code: 'PY101', subject_name: 'Python Programming' },
          { id: '2', subject_code: 'DA201', subject_name: 'Data Analysis Fundamentals' },
          { id: '3', subject_code: 'WD101', subject_name: 'HTML & CSS Basics' }
        ];
        setTrainingSubjects(sampleSubjects);
        console.log('🔧 Using sample training subjects');
      }

    } catch (error) {
      console.error('💥 Error fetching training filter options:', error);

      // Set minimal fallback data so the interface still works
      setTrainingPrograms([
        { id: '1', program_name: 'Data Analysis Training' },
        { id: '2', program_name: 'Web Development Training' }
      ]);
      setTrainingBatches([
        { id: '101', batch_name: 'Batch A' },
        { id: '102', batch_name: 'Batch B' }
      ]);
      setTrainingSubjects([
        { id: '1', subject_code: 'PY101', subject_name: 'Python Programming' },
        { id: '2', subject_code: 'DA201', subject_name: 'Data Analysis' }
      ]);

      console.log('🔧 Fallback training options set');
    }
  };

  const fetchTodayTrainingStats = async () => {
    try {
      const today = new Date().toISOString().split('T')[0];
      const result = await apiCall(`/training-attendance/today-stats?date=${today}`);

      if (result.success) {
        setTodayTrainingStats({
          totalEnrolledStudents: result.data.totalEnrolledStudents || 0,
          studentsPresent: result.data.studentsPresent || 0,
          percentagePresent: result.data.percentagePresent || 0
        });
        console.log('✅ Today training stats updated:', result.data);
      } else {
        console.error('❌ Training stats API failed:', result.message);
        setTodayTrainingStats({
          totalEnrolledStudents: 0,
          studentsPresent: 0,
          percentagePresent: 0
        });
      }
    } catch (error) {
      console.error('Error fetching today training stats:', error);
      // Remove hardcoded fallback values
      setTodayTrainingStats({
        totalEnrolledStudents: 0,
        studentsPresent: 0,
        percentagePresent: 0
      });
    }
  };

  // 🆕 UPDATED: Fetch ALL attendance records from database without any limit
  // Fetch total count only (for initial load)
  const fetchTotalCount = async () => {
    try {
      const result = await apiCall('/attendance/count');
      if (result.success) {
        setAllRecordsCount(result.data.totalCount);
        console.log(`📊 Total records in database: ${result.data.totalCount.toLocaleString()}`);
      }
    } catch (error) {
      console.error('Error fetching total count:', error);
    }
  };

  // Fetch filtered attendance records
  const fetchFilteredAttendanceRecords = async (page = 1) => {
    try {
      setLoading(true);
      setError(null);

      // DECLARE currentSearchTerm ONLY ONCE at the top
      const currentSearchTerm = attendanceType === 'regular' ? searchTerm : trainingSearchTerm;

      // Check if any filters are active
      const hasActiveFilters = filterDepartment || filterDate || filterStudentId.trim() ||
        filterStatus || filterSubject.trim() || filterBatchName.trim() ||
        (currentSearchTerm && currentSearchTerm.trim());

      if (!hasActiveFilters) {
        setDisplayedRecords([]);
        setStatistics({ totalPresent: 0, totalAbsent: 0, totalRecords: 0, attendancePercentage: 0 });
        setCurrentPage(1);
        setTotalPages(0);
        setTotalCount(0);
        setLoading(false);
        return;
      }

      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString()
      });

      // Add active filters
      if (filterDepartment) params.append('department', filterDepartment);
      if (filterDate) params.append('date', filterDate);
      if (filterStudentId.trim()) params.append('student_id', filterStudentId.trim());
      if (filterStatus) params.append('status', filterStatus);
      if (filterSubject.trim()) params.append('subject', filterSubject.trim());
      if (filterBatchName.trim()) params.append('batch_name', filterBatchName.trim());

      // Add search parameter
      if (currentSearchTerm && currentSearchTerm.trim()) {
        params.append('search', currentSearchTerm.trim());
        console.log('ADDING SEARCH PARAMETER:', currentSearchTerm.trim());
      }

      console.log(`Fetching filtered results: ${params.toString()}`);

      const result = await apiCall(`/attendance?${params.toString()}`);

      if (result.success) {
        const { attendance, statistics: stats, pagination } = result.data;

        setDisplayedRecords(attendance);
        setStatistics(stats);
        setCurrentPage(pagination.currentPage);
        setTotalPages(pagination.totalPages);
        setTotalCount(pagination.totalCount);

        console.log(`Filtered results: ${attendance.length} records (${pagination.totalCount} total matches)`);
      }
    } catch (error) {
      console.error('Fetch filtered records error:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };
  // 🆕 UPDATED: Fetch ALL training records from database without any limit
  const fetchAllTrainingRecords = async () => {
    try {
      setTrainingLoading(true);
      setTrainingError(null);

      console.log('📡 Fetching ALL training attendance records...');

      // 🆕 UPDATED: Fetch all training records with unlimited pagination
      let allFetchedRecords = [];
      let currentPage = 1;
      let hasMoreData = true;
      const pageSize = 1000; // Fetch in chunks of 1000 to avoid timeout

      while (hasMoreData) {
        console.log(`📡 Fetching training page ${currentPage} (records ${(currentPage - 1) * pageSize + 1} - ${currentPage * pageSize})...`);

        let result;
        try {
          // Try multiple endpoints for training data
          result = await apiCall(`/training-attendance?page=${currentPage}&limit=${pageSize}`);
        } catch (error) {
          console.log('❌ Primary training endpoint failed, trying alternative...');
          try {
            result = await apiCall(`/training/attendance?page=${currentPage}&limit=${pageSize}`);
          } catch (altError) {
            console.log('❌ Alternative training endpoint also failed, trying basic...');
            // For the first page, try basic endpoint without pagination
            if (currentPage === 1) {
              result = await apiCall('/training-attendance');
              hasMoreData = false; // If basic endpoint, assume we got all data
            } else {
              break; // Stop trying if we're on subsequent pages
            }
          }
        }

        console.log(`📡 Training API Response page ${currentPage}:`, {
          success: result?.success,
          recordsCount: result?.data ? Object.keys(result.data).length : 0
        });

        if (result && result.success) {
          // Handle different response structures
          let pageRecords = [];

          if (result.data?.trainingAttendance) {
            pageRecords = Array.isArray(result.data.trainingAttendance) ? result.data.trainingAttendance : [];
          } else if (result.data?.attendance) {
            pageRecords = Array.isArray(result.data.attendance) ? result.data.attendance : [];
          } else if (result.data && Array.isArray(result.data)) {
            pageRecords = result.data;
          } else if (result.trainingAttendance) {
            pageRecords = Array.isArray(result.trainingAttendance) ? result.trainingAttendance : [];
          } else {
            console.log('⚠️ Unknown training data structure:', result);
            pageRecords = [];
          }

          if (pageRecords.length > 0) {
            allFetchedRecords = [...allFetchedRecords, ...pageRecords];
            console.log(`✅ Training page ${currentPage} loaded: ${pageRecords.length} records. Total so far: ${allFetchedRecords.length}`);

            // Check if we have more pages (if we got less than pageSize, we're done)
            if (pageRecords.length < pageSize) {
              hasMoreData = false;
              console.log('🏁 Reached end of training data');
            } else {
              currentPage++;
            }
          } else {
            hasMoreData = false;
            console.log('🏁 No more training data');
          }
        } else {
          hasMoreData = false;
          const errorMsg = result?.message || 'Failed to fetch training attendance records';
          console.error('❌ Training API error:', errorMsg);
          if (currentPage === 1) {
            setTrainingError(errorMsg);
          }
        }
      }

      console.log(`🎉 TOTAL TRAINING RECORDS FETCHED: ${allFetchedRecords.length}`);

      // FIXED: Enhanced data validation and normalization for training records
      const normalizedRecords = allFetchedRecords.map((record, index) => {
        const normalized = {
          ...record,
          id: record.id || `training_${index}`,
          date: normalizeDate(record.date || record.attendance_date || record.training_date),
          student_id: safeString(record.student_id || record.studentId),
          student_name: safeString(record.student_name || record.studentName || record.name),
          program_name: safeString(record.program_name || record.programName || record.training_program_name),
          subject_name: safeString(record.subject_name || record.subjectName),
          subject_code: safeString(record.subject_code || record.subjectCode || record.subject),
          instructor_name: safeString(record.instructor_name || record.instructorName || record.instructor),
          status: safeString(record.status || record.attendance_status).toLowerCase(),
          training_program_id: safeString(record.training_program_id || record.trainingProgramId || record.program_id),
          training_batch_id: safeString(record.training_batch_id || record.trainingBatchId || record.batch_id),
          training_batch_name: safeString(record.training_batch_name || record.trainingBatchName || record.batch_name),
          session_hours: record.session_hours || record.sessionHours || 1.0,
          location: safeString(record.location || ''),
          remarks: safeString(record.remarks || '')
        };

        // Debug log for first few records
        if (index < 3) {
          console.log(`📋 Training Record ${index + 1} normalized:`, normalized);
        }

        return normalized;
      });

      setAllTrainingRecords(normalizedRecords);

      console.log(`✅ Loaded and normalized ${normalizedRecords.length} training attendance records`);

      // Sample the data structure for debugging
      if (normalizedRecords.length > 0) {
        console.log('📊 Sample training record structure:', normalizedRecords[0]);

        // Check for unique values in key fields
        const uniquePrograms = [...new Set(normalizedRecords.map(r => r.training_program_id).filter(Boolean))];
        const uniqueBatches = [...new Set(normalizedRecords.map(r => r.training_batch_id).filter(Boolean))];
        const uniqueDates = [...new Set(normalizedRecords.map(r => r.date).filter(Boolean))];

        console.log('📊 Training data analysis:', {
          uniquePrograms: uniquePrograms.slice(0, 5),
          uniqueBatches: uniqueBatches.slice(0, 5),
          uniqueDates: uniqueDates.slice(0, 5),
          totalPrograms: uniquePrograms.length,
          totalBatches: uniqueBatches.length,
          totalDates: uniqueDates.length
        });
      }

      if (initialTrainingLoad) {
        setFilteredTrainingRecords([]);
        setDisplayedTrainingRecords([]);
        setTotalCount(0);
        setTotalPages(0);
        setCurrentPage(1);
        setTrainingStatistics({
          totalPresent: 0,
          totalAbsent: 0,
          totalRecords: 0,
          attendancePercentage: 0
        });
        setInitialTrainingLoad(false);
      } else {
        // Apply current filters to new data
        const hasActiveFilters = trainingSearchTerm.trim() || filterTrainingProgram || filterTrainingBatch || filterTrainingDate ||
          filterTrainingStudentId.trim() || filterTrainingStatus || filterTrainingSubject.trim();
        if (hasActiveFilters) {
          console.log('🔄 Applying existing training filters to new data...');
          applyTrainingFilters(normalizedRecords, trainingSearchTerm, filterTrainingProgram, filterTrainingBatch, filterTrainingDate, filterTrainingStudentId, filterTrainingStatus, filterTrainingSubject);
        }
      }

    } catch (error) {
      console.error('💥 Fetch training records error:', error);
      setTrainingError(`Failed to load training attendance records: ${error.message}`);

      // For development - add sample training data if API fails
      if (process.env.NODE_ENV === 'development') {
        console.log('🔧 DEV MODE: Adding sample training data for testing...');
        const sampleTrainingData = [
          {
            id: 'sample_1',
            date: '2025-08-12',
            student_id: 'TR001-2025-001',
            student_name: 'John Doe',
            program_name: 'Data Analysis Training',
            subject_name: 'Python Programming',
            subject_code: 'PY101',
            instructor_name: 'Dr. Smith',
            status: 'present',
            training_program_id: '1',
            training_batch_id: '101',
            training_batch_name: 'Batch A',
            session_hours: 2.0
          },
          {
            id: 'sample_2',
            date: '2025-08-12',
            student_id: 'TR001-2025-002',
            student_name: 'Jane Smith',
            program_name: 'Data Analysis Training',
            subject_name: 'Python Programming',
            subject_code: 'PY101',
            instructor_name: 'Dr. Smith',
            status: 'absent',
            training_program_id: '1',
            training_batch_id: '101',
            training_batch_name: 'Batch A',
            session_hours: 2.0
          }
        ];

        setAllTrainingRecords(sampleTrainingData);
        console.log('🔧 Sample training data loaded for testing');
      }
    } finally {
      setTrainingLoading(false);
    }
  };

  // =============================================
  // RESPONSIVE DESIGN EFFECTS
  // =============================================

  // Check if mobile on mount and resize (used for responsive content layout)
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 1024);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // =============================================
  // ATTENDANCE TYPE HANDLING
  // =============================================

  const handleAttendanceTypeChange = (type) => {
    console.log(`🔄 Switching attendance type to: ${type}`);
    setAttendanceType(type);

    if (type === 'training') {
      if (allTrainingRecords.length === 0) {
        fetchAllTrainingRecords();
      }
      fetchTodayTrainingStats();
    }
  };

  // =============================================
  // EXCEL DOWNLOAD FUNCTION FOR SUBJECT ATTENDANCE
  // =============================================

  const downloadSubjectAttendanceReport = async () => {
    try {
      console.log('📥 Downloading subject attendance report...');

      if (!filterDepartment || !filterSubject) {
        alert('Please select Department and Subject to generate the report');
        return;
      }

      // Build query parameters
      const params = new URLSearchParams({
        department: filterDepartment,
        year: filterDate ? new Date(filterDate).getFullYear() : new Date().getFullYear(),
        subject: filterSubject
      });

      if (filterBatchName) {
        params.append('batch', filterBatchName);
      }

      console.log('Fetching report with params:', params.toString());

      const result = await apiCall(`/subject-attendance-report?${params.toString()}`);

      if (result.success && result.data.reportData.length > 0) {
        const { reportData, summary } = result.data;

        // Create workbook
        const wb = XLSX.utils.book_new();
        const ws = XLSX.utils.json_to_sheet(reportData);

        // Set column widths
        ws['!cols'] = [
          { wch: 12 }, // Roll Number
          { wch: 15 }, // Student ID
          { wch: 25 }, // Student Name
          { wch: 30 }, // Email
          { wch: 20 }, // Department
          { wch: 8 },  // Year
          { wch: 15 }, // Batch
          { wch: 30 }, // Subject
          { wch: 12 }, // Total Classes
          { wch: 15 }, // Classes Attended
          { wch: 14 }, // Classes Absent
          { wch: 12 }, // Attendance %
          { wch: 10 }  // Below 50%
        ];

        XLSX.utils.book_append_sheet(wb, ws, 'Attendance Report');

        // Generate filename
        const filename = `Subject_Attendance_${summary.department}_${summary.subject}_${new Date().toISOString().split('T')[0]}.xlsx`;

        // Download the file
        XLSX.writeFile(wb, filename);

        console.log('✅ Excel file downloaded successfully');
        alert(`Report generated successfully!\\n\\nTotal Students: ${summary.totalStudents}\\nStudents Below 50%: ${summary.studentsBelow50}\\nTotal Classes: ${summary.totalClasses}`);
      } else {
        alert(result.message || 'No data available for the selected filters');
      }
    } catch (error) {
      console.error('💥 Error downloading report:', error);
      alert(`Failed to download report: ${error.message}`);
    }
  };

  // =============================================
  // ATTENDANCE ANALYTICS FUNCTIONS
  // =============================================

  const fetchAttendanceAnalytics = async (department, subject = '') => {
    if (!department) {
      setShowAnalytics(false);
      setAnalyticsData(null);
      return;
    }

    try {
      setAnalyticsLoading(true);
      console.log('📊 Fetching attendance analytics for:', { department, subject });

      let url = `/attendance-analytics?department=${encodeURIComponent(department)}`;
      if (subject) {
        url += `&subject=${encodeURIComponent(subject)}`;
      }

      const result = await apiCall(url);

      if (result.success) {
        setAnalyticsData(result.data);
        setShowAnalytics(true);
        console.log('✅ Analytics loaded:', result.data);
      } else {
        console.error('❌ Analytics fetch failed:', result.message);
        setShowAnalytics(false);
        setAnalyticsData(null);
      }
    } catch (error) {
      console.error('💥 Error fetching analytics:', error);
      setShowAnalytics(false);
      setAnalyticsData(null);
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const downloadCategoryReport = async (category, categoryName) => {
    if (!filterDepartment) {
      alert('Please select a department first');
      return;
    }

    try {
      console.log(`📥 Downloading ${categoryName} report...`);

      const result = await apiCall(`/attendance-download-by-category?department=${encodeURIComponent(filterDepartment)}&category=${category}`);

      if (result.success && result.data.reportData.length > 0) {
        const { reportData, summary } = result.data;

        // Create workbook
        const wb = XLSX.utils.book_new();
        const ws = XLSX.utils.json_to_sheet(reportData);

        // Set column widths
        ws['!cols'] = [
          { wch: 12 }, // Roll Number
          { wch: 15 }, // Student ID
          { wch: 25 }, // Student Name
          { wch: 30 }, // Email
          { wch: 20 }, // Department
          { wch: 8 },  // Year
          { wch: 15 }, // Batch
          { wch: 12 }, // Total Classes
          { wch: 15 }, // Classes Attended
          { wch: 14 }, // Classes Absent
          { wch: 12 }, // Attendance %
          { wch: 15 }  // Category
        ];

        XLSX.utils.book_append_sheet(wb, ws, categoryName);

        // Generate filename
        const filename = `Attendance_${categoryName.replace(/[^a-zA-Z0-9]/g, '_')}_${summary.department}_${new Date().toISOString().split('T')[0]}.xlsx`;

        // Download the file
        XLSX.writeFile(wb, filename);

        console.log('✅ Excel file downloaded successfully');
        alert(`Report generated successfully!\n\nCategory: ${categoryName}\nTotal Students: ${summary.totalStudents}`);
      } else {
        alert(result.message || 'No students found in this category');
      }
    } catch (error) {
      console.error('💥 Error downloading category report:', error);
      alert(`Failed to download report: ${error.message}`);
    }
  };

  const fetchSubjectsList = async (department) => {
    if (!department) {
      setSubjectsList([]);
      setRegularSubjectsList([]);
      return;
    }

    try {
      setSubjectsListLoading(true);
      setRegularSubjectsLoading(true);

      console.log('📚 Fetching regular subjects for department:', department);

      // Use the NEW endpoint that only requires department
      const result = await apiCall(`/regular-subjects?department=${encodeURIComponent(department)}`);

      console.log('📚 Regular subjects API response:', result);

      if (result.success && result.data) {
        const subjects = result.data.map(s => ({
          value: s.subject_name || s.subject_code,
          label: s.subject_name || s.subject_code
        }));

        console.log('✅ Loaded subjects:', subjects.length);
        setSubjectsList(subjects);
        setRegularSubjectsList(subjects);
      } else {
        console.warn('⚠️ No subjects found or API returned unsuccessful');
        setSubjectsList([]);
        setRegularSubjectsList([]);
      }
    } catch (error) {
      console.error('💥 Error fetching subjects list:', error);
      setSubjectsList([]);
      setRegularSubjectsList([]);
    } finally {
      setSubjectsListLoading(false);
      setRegularSubjectsLoading(false);
    }
  };

  const fetchSubjectWiseAttendanceAnalytics = async () => {
    if (!analyticsFilterDept) {
      alert('Please select a department');
      return;
    }

    try {
      setSubjectWiseLoading(true);
      console.log('📊 Fetching subject-wise analytics:', { dept: analyticsFilterDept, subject: analyticsFilterSubject });

      // Use the NEW endpoint for subject-wise analytics
      let url = `/subject-wise-attendance-analytics?department=${encodeURIComponent(analyticsFilterDept)}`;
      if (analyticsFilterSubject && analyticsFilterSubject !== '') {
        url += `&subject=${encodeURIComponent(analyticsFilterSubject)}`;
      }

      const result = await apiCall(url);

      console.log('📊 Subject-wise analytics response:', result);

      if (result.success) {
        setSubjectWiseData(result.data);
        setShowSubjectWiseAnalytics(true);
        setExpandedCategory(null); // Reset expanded category
      } else {
        alert(result.message || 'Failed to fetch analytics');
        setShowSubjectWiseAnalytics(false);
      }
    } catch (error) {
      console.error('💥 Error fetching subject-wise analytics:', error);
      alert('Error fetching analytics: ' + error.message);
      setShowSubjectWiseAnalytics(false);
    } finally {
      setSubjectWiseLoading(false);
    }
  };

  const downloadSubjectWiseReport = async (category, categoryName) => {
    if (!analyticsFilterDept) {
      alert('Please select a department first');
      return;
    }

    try {
      console.log(`📥 Downloading ${categoryName} report...`);

      // Use the NEW endpoint for category downloads
      let url = `/subject-wise-download-category?department=${encodeURIComponent(analyticsFilterDept)}&category=${category}`;
      if (analyticsFilterSubject && analyticsFilterSubject !== '') {
        url += `&subject=${encodeURIComponent(analyticsFilterSubject)}`;
      }

      const result = await apiCall(url);

      if (result.success && result.data.reportData?.length > 0) {
        const { reportData, summary } = result.data;

        // Create workbook
        const wb = XLSX.utils.book_new();
        const ws = XLSX.utils.json_to_sheet(reportData);

        // Set column widths
        ws['!cols'] = [
          { wch: 6 },   // S.No
          { wch: 15 },  // Roll Number
          { wch: 18 },  // Student ID
          { wch: 25 },  // Student Name
          { wch: 30 },  // Email
          { wch: 15 },  // Phone
          { wch: 20 },  // Department
          { wch: 6 },   // Year
          { wch: 15 },  // Batch
          { wch: 25 },  // Subject
          { wch: 12 },  // Total Classes
          { wch: 15 },  // Classes Attended
          { wch: 12 },  // Classes Absent
          { wch: 12 },  // Attendance %
          { wch: 15 }   // Status
        ];

        XLSX.utils.book_append_sheet(wb, ws, categoryName.substring(0, 31));

        // Generate filename
        const subjectPart = analyticsFilterSubject ? `_${analyticsFilterSubject.replace(/[^a-zA-Z0-9]/g, '_')}` : '';
        const filename = `Attendance_${category}${subjectPart}_${summary.department}_${new Date().toISOString().split('T')[0]}.xlsx`;

        // Download the file
        XLSX.writeFile(wb, filename);

        console.log('✅ Excel file downloaded successfully');
        alert(`Report Generated Successfully!\n\nCategory: ${categoryName}\nDepartment: ${summary.department}\nSubject: ${summary.subject}\nTotal Students: ${summary.totalStudents}`);
      } else {
        alert('No students found in this category');
      }
    } catch (error) {
      console.error('💥 Error downloading category report:', error);
      alert(`Failed to download report: ${error.message}`);
    }
  };

  const downloadFullSubjectReport = async () => {
    if (!analyticsFilterDept || !analyticsFilterSubject) {
      alert('Please select department and subject');
      return;
    }
    try {
      const result = await apiCall(`/subject-attendance-report?department=${encodeURIComponent(analyticsFilterDept)}&subject=${encodeURIComponent(analyticsFilterSubject)}`);
      if (result.success && result.data.reportData?.length > 0) {
        const { reportData } = result.data;
        const wb = XLSX.utils.book_new();
        const ws = XLSX.utils.json_to_sheet(reportData);
        XLSX.utils.book_append_sheet(wb, ws, "Full Report");
        XLSX.writeFile(wb, `Full_Attendance_Report_${analyticsFilterSubject}.xlsx`);
      } else {
        alert('No data found');
      }
    } catch (error) {
      console.error('Download error:', error);
      alert('Failed to download report');
    }
  };

  // =============================================
  // FILTER HANDLERS - FIXED
  // =============================================

  const handleClearFilters = () => {
    console.log('🗑️ Clearing all filters');

    if (attendanceType === 'regular') {
      setFilterDepartment('');
      setFilterDate('');
      setFilterStudentId('');
      setFilterStatus('');
      setFilterSubject('');
      setFilterBatchName('');
      setSearchTerm('');

      setFilteredRecords([]);
      setDisplayedRecords([]);
      setTotalCount(0);
      setTotalPages(0);
      setCurrentPage(1);
      setStatistics({
        totalPresent: 0,
        totalAbsent: 0,
        totalRecords: 0,
        attendancePercentage: 0
      });
    } else {
      setFilterTrainingProgram('');
      setFilterTrainingBatch('');
      setFilterTrainingDate('');
      setFilterTrainingStudentId('');
      setFilterTrainingStatus('');
      setFilterTrainingSubject('');
      setTrainingSearchTerm('');

      setFilteredTrainingRecords([]);
      setDisplayedTrainingRecords([]);
      setTotalCount(0);
      setTotalPages(0);
      setCurrentPage(1);
      setTrainingStatistics({
        totalPresent: 0,
        totalAbsent: 0,
        totalRecords: 0,
        attendancePercentage: 0
      });
    }
  };

  // =============================================
  // NEW: SUBJECT-WISE ANALYTICS HIERARCHICAL FETCH FUNCTIONS
  // =============================================

  // Fetch batches for subject analytics (filtered by department and year)
  const fetchSubjectAnalyticsBatches = async (department, year) => {
    if (!department || !year) {
      setSubjectAnalyticsBatches([]);
      return;
    }

    try {
      console.log('📚 Fetching batches for subject analytics:', { department, year });
      const dbYear = 2000 + parseInt(year); // Convert UI year (1,2,3,4) to DB year (2001,2002,2003,2004)
      const result = await apiCall(`/batches?department=${encodeURIComponent(department)}&year=${dbYear}`);

      if (result.success && result.data) {
        console.log('✅ Subject analytics batches loaded:', result.data);
        setSubjectAnalyticsBatches(result.data);
      } else {
        setSubjectAnalyticsBatches([]);
      }
    } catch (error) {
      console.error('💥 Error fetching subject analytics batches:', error);
      setSubjectAnalyticsBatches([]);
    }
  };

  // Fetch subjects for subject analytics (filtered by department, year, and batch)
  const fetchSubjectAnalyticsSubjects = async (department, year, batch) => {
    if (!department || !year || !batch) {
      setSubjectAnalyticsSubjects([]);
      return;
    }

    try {
      console.log('📚 Fetching subjects for analytics:', { department, year, batch });
      const dbYear = 2000 + parseInt(year); // Convert UI year to DB year
      const result = await apiCall(`/regular-subjects?department=${encodeURIComponent(department)}&year=${dbYear}&batch=${encodeURIComponent(batch)}`);

      if (result.success && result.data) {
        const subjects = result.data.map(s => ({
          value: s.subject_name || s.subject_code,
          label: s.subject_name || s.subject_code
        }));
        console.log('✅ Subject analytics subjects loaded:', subjects.length);
        setSubjectAnalyticsSubjects(subjects);
      } else {
        setSubjectAnalyticsSubjects([]);
      }
    } catch (error) {
      console.error('💥 Error fetching subject analytics subjects:', error);
      setSubjectAnalyticsSubjects([]);
    }
  };

  // Fetch analytics data for selected subject
  const fetchSubjectAnalytics = async () => {
    if (!subjectAnalyticsDept || !subjectAnalyticsYear || !subjectAnalyticsBatch || !subjectAnalyticsSubject) {
      setSubjectAnalyticsData(null);
      return;
    }

    try {
      setSubjectAnalyticsLoading(true);
      console.log('📊 Fetching subject analytics:', {
        dept: subjectAnalyticsDept,
        year: subjectAnalyticsYear,
        batch: subjectAnalyticsBatch,
        subject: subjectAnalyticsSubject,
        fromDate: subjectAnalyticsFromDate,
        toDate: subjectAnalyticsToDate
      });

      let url = `/subject-wise-attendance-analytics?department=${encodeURIComponent(subjectAnalyticsDept)}&subject=${encodeURIComponent(subjectAnalyticsSubject)}`;

      if (subjectAnalyticsFromDate) url += `&fromDate=${subjectAnalyticsFromDate}`;
      if (subjectAnalyticsToDate) url += `&toDate=${subjectAnalyticsToDate}`;

      const result = await apiCall(url);

      if (result.success) {
        console.log('✅ Subject analytics data loaded:', result.data);
        setSubjectAnalyticsData(result.data);
      } else {
        alert(result.message || 'Failed to fetch analytics');
        setSubjectAnalyticsData(null);
      }
    } catch (error) {
      console.error('💥 Error fetching subject analytics:', error);
      alert('Error fetching analytics: ' + error.message);
      setSubjectAnalyticsData(null);
    } finally {
      setSubjectAnalyticsLoading(false);
    }
  };

  // Download category report for subject analytics
  const downloadSubjectAnalyticsCategory = async (category, categoryName) => {
    if (!subjectAnalyticsDept || !subjectAnalyticsSubject) {
      alert('Please select department and subject');
      return;
    }

    try {
      console.log('📥 Downloading subject analytics category:', { category, dept: subjectAnalyticsDept, subject: subjectAnalyticsSubject });

      let url = `/subject-wise-download-category?department=${encodeURIComponent(subjectAnalyticsDept)}&subject=${encodeURIComponent(subjectAnalyticsSubject)}&category=${category}`;

      if (subjectAnalyticsFromDate) url += `&fromDate=${subjectAnalyticsFromDate}`;
      if (subjectAnalyticsToDate) url += `&toDate=${subjectAnalyticsToDate}`;

      const result = await apiCall(url);

      if (result.success && result.data.reportData?.length > 0) {
        const { reportData, summary } = result.data;

        // Create workbook and worksheet
        const wb = XLSX.utils.book_new();
        const ws = XLSX.utils.json_to_sheet(reportData);

        // Set column widths
        ws['!cols'] = [
          { wch: 6 },   // S.No
          { wch: 15 },  // Roll Number
          { wch: 20 },  // Student ID
          { wch: 25 },  // Student Name
          { wch: 30 },  // Email
          { wch: 15 },  // Phone
          { wch: 20 },  // Department
          { wch: 6 },   // Year
          { wch: 15 },  // Batch
          { wch: 25 },  // Subject
          { wch: 12 },  // Total Classes
          { wch: 15 },  // Classes Attended
          { wch: 12 },  // Classes Absent
          { wch: 12 },  // Attendance %
          { wch: 15 }   // Status
        ];

        XLSX.utils.book_append_sheet(wb, ws, categoryName.substring(0, 31));

        // Generate filename
        const filename = `Subject_Attendance_${category}_${summary.department}_${subjectAnalyticsSubject.replace(/[^a-zA-Z0-9]/g, '_')}_${new Date().toISOString().split('T')[0]}.xlsx`;

        // Download the file
        XLSX.writeFile(wb, filename);

        console.log('✅ Excel file downloaded successfully');
        alert(`Report Generated Successfully!\n\nCategory: ${categoryName}\nDepartment: ${summary.department}\nSubject: ${summary.subject}\nTotal Students: ${summary.totalStudents}`);
      } else {
        alert('No students found in this category');
      }
    } catch (error) {
      console.error('💥 Error downloading category report:', error);
      alert(`Failed to download report: ${error.message}`);
    }
  };

  // Handle department change for subject analytics
  const handleSubjectAnalyticsDeptChange = (value) => {
    setSubjectAnalyticsDept(value);
    setSubjectAnalyticsYear('');
    setSubjectAnalyticsBatch('');
    setSubjectAnalyticsSubject('');
    setSubjectAnalyticsBatches([]);
    setSubjectAnalyticsSubjects([]);
    setSubjectAnalyticsData(null);
  };

  // Handle year change for subject analytics
  const handleSubjectAnalyticsYearChange = (value) => {
    setSubjectAnalyticsYear(value);
    setSubjectAnalyticsBatch('');
    setSubjectAnalyticsSubject('');
    setSubjectAnalyticsSubjects([]);
    setSubjectAnalyticsData(null);

    if (value && subjectAnalyticsDept) {
      fetchSubjectAnalyticsBatches(subjectAnalyticsDept, value);
    } else {
      setSubjectAnalyticsBatches([]);
    }
  };

  // Handle batch change for subject analytics
  const handleSubjectAnalyticsBatchChange = (value) => {
    setSubjectAnalyticsBatch(value);
    setSubjectAnalyticsSubject('');
    setSubjectAnalyticsData(null);

    if (value && subjectAnalyticsDept && subjectAnalyticsYear) {
      fetchSubjectAnalyticsSubjects(subjectAnalyticsDept, subjectAnalyticsYear, value);
    } else {
      setSubjectAnalyticsSubjects([]);
    }
  };

  // Handle subject change for subject analytics
  const handleSubjectAnalyticsSubjectChange = (value) => {
    setSubjectAnalyticsSubject(value);
    setSubjectAnalyticsData(null);
  };

  // Auto-fetch analytics when all filters are selected
  useEffect(() => {
    if (subjectAnalyticsDept && subjectAnalyticsYear && subjectAnalyticsBatch && subjectAnalyticsSubject) {
      fetchSubjectAnalytics();
    }
  }, [subjectAnalyticsDept, subjectAnalyticsYear, subjectAnalyticsBatch, subjectAnalyticsSubject, subjectAnalyticsFromDate, subjectAnalyticsToDate]);

  // FIXED: Enhanced useEffect for REGULAR attendance filtering with better logging
  useEffect(() => {
    if (attendanceType === 'regular' && allRecords.length > 0) {
      const hasActiveFilters = searchTerm.trim() || filterDepartment || filterDate ||
        filterStudentId.trim() || filterStatus || filterSubject.trim() || filterBatchName.trim();

      console.log('🔄 REGULAR FILTER EFFECT TRIGGERED:', {
        attendanceType,
        recordsCount: allRecords.length,
        hasActiveFilters,
        filters: {
          searchTerm: searchTerm.trim(),
          filterDepartment,
          filterDate,
          filterStudentId: filterStudentId.trim(),
          filterStatus,
          filterSubject: filterSubject.trim(),
          filterBatchName: filterBatchName.trim()
        }
      });

      if (hasActiveFilters) {
        console.log('✅ APPLYING REGULAR FILTERS');
        applyFilters(allRecords, searchTerm, filterDepartment, filterDate, filterStudentId, filterStatus, filterSubject, filterBatchName);
      } else {
        console.log('❌ NO ACTIVE FILTERS - CLEARING DISPLAY');
        setFilteredRecords([]);
        setDisplayedRecords([]);
        setTotalCount(0);
        setTotalPages(0);
        setCurrentPage(1);
        setStatistics({
          totalPresent: 0,
          totalAbsent: 0,
          totalRecords: 0,
          attendancePercentage: 0
        });
      }
    }
  }, [searchTerm, filterDepartment, filterDate, filterStudentId, filterStatus, filterSubject, filterBatchName, allRecords, attendanceType]);

  // Fetch attendance analytics when department or subject changes
  useEffect(() => {
    if (attendanceType === 'regular' && filterDepartment) {
      fetchAttendanceAnalytics(filterDepartment, filterSubject);
    } else {
      setShowAnalytics(false);
      setAnalyticsData(null);
    }
  }, [filterDepartment, filterSubject, attendanceType]);

  // FIXED: Enhanced useEffect for TRAINING attendance filtering with better logging
  useEffect(() => {
    if (attendanceType === 'training' && allTrainingRecords.length > 0) {
      const hasActiveFilters = trainingSearchTerm.trim() || filterTrainingProgram || filterTrainingBatch || filterTrainingDate ||
        filterTrainingStudentId.trim() || filterTrainingStatus || filterTrainingSubject.trim();

      console.log('🔄 TRAINING FILTER EFFECT TRIGGERED:', {
        attendanceType,
        recordsCount: allTrainingRecords.length,
        hasActiveFilters,
        filters: {
          trainingSearchTerm: trainingSearchTerm.trim(),
          filterTrainingProgram,
          filterTrainingBatch,
          filterTrainingDate,
          filterTrainingStudentId: filterTrainingStudentId.trim(),
          filterTrainingStatus,
          filterTrainingSubject: filterTrainingSubject.trim()
        }
      });

      if (hasActiveFilters) {
        console.log('✅ APPLYING TRAINING FILTERS');
        applyTrainingFilters(allTrainingRecords, trainingSearchTerm, filterTrainingProgram, filterTrainingBatch, filterTrainingDate, filterTrainingStudentId, filterTrainingStatus, filterTrainingSubject);
      } else {
        console.log('❌ NO ACTIVE TRAINING FILTERS - CLEARING DISPLAY');
        setFilteredTrainingRecords([]);
        setDisplayedTrainingRecords([]);
        setTotalCount(0);
        setTotalPages(0);
        setCurrentPage(1);
        setTrainingStatistics({
          totalPresent: 0,
          totalAbsent: 0,
          totalRecords: 0,
          attendancePercentage: 0
        });
      }
    }
  }, [trainingSearchTerm, filterTrainingProgram, filterTrainingBatch, filterTrainingDate, filterTrainingStudentId, filterTrainingStatus, filterTrainingSubject, allTrainingRecords, attendanceType]);

  // Handle pagination changes
  const handlePageChange = (page) => {
    if (page && page >= 1 && page <= totalPages) {
      if (attendanceType === 'regular') {
        fetchFilteredAttendanceRecords(page);
      } else {
        applyTrainingPagination(filteredTrainingRecords, page);
      }
    }
  };

  // Handle search manually (for search button)
  const handleSearch = () => {
    console.log('🔍 Manual search triggered');
    if (attendanceType === 'regular' && allRecords.length > 0) {
      applyFilters(allRecords, searchTerm, filterDepartment, filterDate, filterStudentId, filterStatus, filterSubject, filterBatchName);
    } else if (attendanceType === 'training' && allTrainingRecords.length > 0) {
      applyTrainingFilters(allTrainingRecords, trainingSearchTerm, filterTrainingProgram, filterTrainingBatch, filterTrainingDate, filterTrainingStudentId, filterTrainingStatus, filterTrainingSubject);
    }
  };

  // Refresh records (re-fetch from server)
  const refreshRecords = () => {
    console.log('🔄 Refreshing ALL records from database...');
    if (attendanceType === 'regular') {
      setInitialLoad(false);
      fetchFilteredAttendanceRecords(1);
    } else {
      setInitialTrainingLoad(false);
      fetchAllTrainingRecords();
      fetchTodayTrainingStats();
    }
  };

  // =============================================
  // FORM FUNCTIONS (Keeping existing logic)
  // =============================================

  const validateForm = () => {
    const errors = {};

    if (attendanceType === 'regular') {
      if (!formData.date.trim()) errors.date = 'Date is required';
      if (!formData.department.trim()) errors.department = 'Department is required';
      if (!formData.year.trim()) errors.year = 'Year is required';
      if (!formData.batch_name.trim()) errors.batch_name = 'Batch name is required';
      if (!formData.student_id.trim()) errors.student_id = 'Student ID is required';
      if (!formData.status.trim()) errors.status = 'Status is required';
      if (!formData.subject.trim()) errors.subject = 'Subject is required';

      if (formData.date && new Date(formData.date) > new Date()) {
        errors.date = 'Date cannot be in the future';
      }

      if (formData.student_id && formData.student_id.length < 3) {
        errors.student_id = 'Student ID must be at least 3 characters';
      }
    } else {
      if (!trainingFormData.date.trim()) errors.date = 'Date is required';
      if (!trainingFormData.training_program_id.trim()) errors.training_program_id = 'Training program is required';
      if (!trainingFormData.training_batch_id.trim()) errors.training_batch_id = 'Training batch is required';
      if (!trainingFormData.student_id.trim()) errors.student_id = 'Student ID is required';
      if (!trainingFormData.status.trim()) errors.status = 'Status is required';
      if (!trainingFormData.subject_code.trim()) errors.subject_code = 'Subject is required';

      if (trainingFormData.date && new Date(trainingFormData.date) > new Date()) {
        errors.date = 'Date cannot be in the future';
      }

      if (trainingFormData.student_id && trainingFormData.student_id.length < 3) {
        errors.student_id = 'Student ID must be at least 3 characters';
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    console.log('🚀 SUBMIT: Starting form submission...');

    if (!validateForm()) {
      console.log('❌ SUBMIT: Form validation failed');
      return;
    }

    try {
      setSubmitting(true);

      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      if (!token) {
        alert('Authentication token not found. Please login again.');
        return;
      }

      if (attendanceType === 'regular') {
        const recordId = selectedRecord?.id;
        const url = selectedRecord
          ? `${API_LINK}/api/admin/attendance/${recordId}`
          : `${API_LINK}/api/admin/attendance`;

        const method = selectedRecord ? 'PUT' : 'POST';

        const requestBody = {
          date: formData.date,
          department: formData.department,
          year: formData.year,
          batch_name: formData.batch_name,
          student_id: formData.student_id,
          status: formData.status,
          subject: formData.subject
        };

        const response = await fetch(url, {
          method,
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify(requestBody)
        });

        const result = await response.json();

        if (response.ok && result.success) {
          setShowAddModal(false);
          setShowEditModal(false);
          resetForm();

          setInitialLoad(false);
          await fetchFilteredAttendanceRecords(1);

          alert(selectedRecord ? 'Attendance record updated successfully!' : 'Attendance record added successfully!');
        } else {
          let errorMessage = result.message || 'Operation failed';
          if (result.error) {
            errorMessage += `: ${result.error}`;
          }
          alert(`Error: ${errorMessage}`);
        }
      } else {
        const url = selectedRecord
          ? `/training-attendance/${selectedRecord.id}`
          : '/training-attendance';

        const method = selectedRecord ? 'PUT' : 'POST';

        const result = await apiCall(url, {
          method,
          body: JSON.stringify(trainingFormData)
        });

        if (result.success) {
          setShowAddModal(false);
          setShowEditModal(false);
          resetForm();

          setInitialTrainingLoad(false);
          await fetchAllTrainingRecords();
          fetchTodayTrainingStats();

          alert(selectedRecord ? 'Training attendance record updated successfully!' : 'Training attendance record added successfully!');
        } else {
          alert(`Error: ${result.message}`);
        }
      }
    } catch (error) {
      console.error('💥 SUBMIT: Submit error:', error);
      alert(`Failed to save attendance record: ${error.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedRecord || !selectedRecord.id) {
      alert('No record selected for deletion');
      return;
    }

    try {
      setSubmitting(true);

      if (attendanceType === 'regular') {
        const token = localStorage.getItem('token') || sessionStorage.getItem('token');

        const response = await fetch(`${API_LINK}/api/admin/attendance/${selectedRecord.id}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        const result = await response.json();

        if (result.success) {
          setShowDeleteModal(false);
          setSelectedRecord(null);

          setInitialLoad(false);
          await fetchFilteredAttendanceRecords(1);

          alert('Attendance record deleted successfully!');
        } else {
          alert(result.message || 'Delete failed');
        }
      } else {
        const result = await apiCall(`/training-attendance/${selectedRecord.id}`, {
          method: 'DELETE'
        });

        if (result.success) {
          setShowDeleteModal(false);
          setSelectedRecord(null);

          setInitialTrainingLoad(false);
          await fetchAllTrainingRecords();
          fetchTodayTrainingStats();

          alert('Training attendance record deleted successfully!');
        } else {
          alert(result.message || 'Delete failed');
        }
      }
    } catch (error) {
      console.error('💥 DELETE: Delete error:', error);
      alert(`Failed to delete attendance record: ${error.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      date: new Date().toISOString().split('T')[0],
      department: '',
      year: '',
      batch_name: '',
      student_id: '',
      status: 'present',
      subject: ''
    });
    setTrainingFormData({
      date: new Date().toISOString().split('T')[0],
      training_program_id: '',
      training_batch_id: '',
      student_id: '',
      status: 'present',
      subject_code: '',
      session_hours: 1.0,
      remarks: ''
    });
    setFormErrors({});
    setSelectedRecord(null);
    setResolvedStudent(null);
  };

  const handleEditClick = (record) => {
    if (!record || !record.id) {
      alert('Invalid record selected for editing');
      return;
    }

    setSelectedRecord(record);

    if (attendanceType === 'regular') {
      // Fix Date formatting: Use the date part directly if ISO, or ensure local date
      let formattedDate = '';
      if (record.date) {
        try {
          // If it's YYYY-MM-DD string, use it directly
          if (/^\d{4}-\d{2}-\d{2}$/.test(record.date)) {
            formattedDate = record.date;
          } else {
            // If ISO string, take the date part (UTC) to avoid timezone shifts
            // e.g. 2026-02-09T00:00:00.000Z -> 2026-02-09
            const d = new Date(record.date);
            if (!isNaN(d.getTime())) {
              const year = d.getFullYear();
              const month = String(d.getMonth() + 1).padStart(2, '0');
              const day = String(d.getDate()).padStart(2, '0');
              formattedDate = `${year}-${month}-${day}`;
            } else {
              formattedDate = record.date;
            }
          }
        } catch (e) {
          formattedDate = record.date || '';
        }
      }

      // Year Logic: Use the value directly (supports both 1-4 and 2001-2004)
      let yearVal = record.year?.toString() || '';

      setFormData({
        date: formattedDate || new Date().toISOString().split('T')[0],
        department: record.department || '',
        year: yearVal,
        batch_name: record.batch_name || '',
        student_id: record.student_id || '',
        status: record.status || 'present',
        subject: record.subject || ''
      });
    } else {
      setTrainingFormData({
        date: record.date || new Date().toISOString().split('T')[0],
        training_program_id: record.training_program_id?.toString() || '',
        training_batch_id: record.training_batch_id?.toString() || '',
        student_id: record.student_id || '',
        status: record.status || 'present',
        subject_code: record.subject_code || '',
        session_hours: record.session_hours || 1.0,
        remarks: record.remarks || ''
      });
    }

    setFormErrors({});
    setShowEditModal(true);
  };

  const handleDeleteClick = (record) => {
    if (!record || !record.id) {
      alert('Invalid record selected for deletion');
      return;
    }
    setSelectedRecord(record);
    setShowDeleteModal(true);
  };



  // =============================================
  // INITIAL DATA LOADING
  // =============================================

  useEffect(() => {
    fetchDepartments();
    fetchTrainingFilterOptions();
    fetchTotalCount(); // Get total count on initial load
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (attendanceType === 'regular') {
        fetchFilteredAttendanceRecords(1);
      }
    }, 500); // 500ms debounce

    return () => clearTimeout(timer);
  }, [attendanceType, filterDepartment, filterDate, filterStudentId, filterStatus, filterSubject, filterBatchName, searchTerm]);

  // =============================================
  // COMPONENT STATE CALCULATIONS
  // =============================================

  // Check if any filters are active
  const hasActiveFilters = attendanceType === 'regular'
    ? (searchTerm.trim() || filterDepartment || filterDate ||
      filterStudentId.trim() || filterStatus || filterSubject.trim() || filterBatchName.trim())
    : (trainingSearchTerm.trim() || filterTrainingProgram || filterTrainingBatch || filterTrainingDate ||
      filterTrainingStudentId.trim() || filterTrainingStatus || filterTrainingSubject.trim());

  const currentRecords = attendanceType === 'regular' ? allRecords : allTrainingRecords;
  const currentDisplayedRecords = attendanceType === 'regular' ? displayedRecords : displayedTrainingRecords;
  const currentStatistics = attendanceType === 'regular' ? statistics : trainingStatistics;
  const currentLoading = attendanceType === 'regular' ? loading : trainingLoading;
  const currentError = attendanceType === 'regular' ? error : trainingError;

  // Configuration data
  const years = ['1', '2', '3', '4', '2001', '2002', '2003', '2004'];

  // =============================================
  // COMPONENT RENDER
  // =============================================

  return (
    <Layout role="admin" onNavigate={onNavigate} activePage="admin-attendance" userInfo={userInfo}>

      {/* Attendance Type Tabs */}
      <div style={{
        background: 'linear-gradient(135deg, #238548 0%, #1a6635 100%)',
        padding: '2rem',
        borderRadius: '12px',
        marginBottom: '2rem',
        boxShadow: '0 4px 15px rgba(0,0,0,0.1)'
      }}>
        <h2 style={{
          margin: '0 0 1.5rem 0',
          fontSize: '1.5rem',
          fontWeight: 600,
          color: '#ffffff',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem'
        }}>
          🎯 Attendance Type
        </h2>

        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
          gap: '1rem'
        }}>
          {/* Regular Classes Tab */}
          <button
            onClick={() => setAttendanceType('regular')}
            style={{
              padding: '1.5rem',
              background: attendanceType === 'regular'
                ? '#ffffff'
                : 'rgba(255, 255, 255, 0.2)',
              border: attendanceType === 'regular'
                ? '3px solid #ffffff'
                : '2px solid rgba(255, 255, 255, 0.3)',
              borderRadius: '12px',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              textAlign: 'center'
            }}
            onMouseEnter={(e) => {
              if (attendanceType !== 'regular') {
                e.target.style.background = 'rgba(255, 255, 255, 0.3)';
                e.target.style.transform = 'translateY(-2px)';
              }
            }}
            onMouseLeave={(e) => {
              if (attendanceType !== 'regular') {
                e.target.style.background = 'rgba(255, 255, 255, 0.2)';
                e.target.style.transform = 'translateY(0)';
              }
            }}
          >
            <div style={{
              fontSize: '2rem',
              marginBottom: '0.5rem'
            }}>
              📚
            </div>
            <div style={{
              fontSize: '1.2rem',
              fontWeight: 600,
              color: attendanceType === 'regular' ? '#238548' : '#ffffff',
              marginBottom: '0.25rem'
            }}>
              Regular Classes
            </div>
            <div style={{
              fontSize: '0.85rem',
              color: attendanceType === 'regular' ? '#666' : 'rgba(255, 255, 255, 0.8)'
            }}>
              Take attendance for all students in their regular batch classes
            </div>
          </button>

          {/* Training Programs Tab */}
          <button
            onClick={() => setAttendanceType('training')}
            style={{
              padding: '1.5rem',
              background: attendanceType === 'training'
                ? '#ffffff'
                : 'rgba(255, 255, 255, 0.2)',
              border: attendanceType === 'training'
                ? '3px solid #ffffff'
                : '2px solid rgba(255, 255, 255, 0.3)',
              borderRadius: '12px',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              textAlign: 'center'
            }}
            onMouseEnter={(e) => {
              if (attendanceType !== 'training') {
                e.target.style.background = 'rgba(255, 255, 255, 0.3)';
                e.target.style.transform = 'translateY(-2px)';
              }
            }}
            onMouseLeave={(e) => {
              if (attendanceType !== 'training') {
                e.target.style.background = 'rgba(255, 255, 255, 0.2)';
                e.target.style.transform = 'translateY(0)';
              }
            }}
          >
            <div style={{
              fontSize: '2rem',
              marginBottom: '0.5rem'
            }}>
              💼
            </div>
            <div style={{
              fontSize: '1.2rem',
              fontWeight: 600,
              color: attendanceType === 'training' ? '#238548' : '#ffffff',
              marginBottom: '0.25rem'
            }}>
              Training Programs
            </div>
            <div style={{
              fontSize: '0.85rem',
              color: attendanceType === 'training' ? '#666' : 'rgba(255, 255, 255, 0.8)'
            }}>
              Take attendance for specialized training sessions
            </div>
          </button>
        </div>
      </div>

      {/* Filter Attendance Records Section */}
      <div style={{
        background: '#ffffff',
        padding: '2rem',
        borderRadius: '12px',
        marginBottom: '2rem',
        boxShadow: '0 4px 15px rgba(0,0,0,0.1)'
      }}>
        <h3 style={{
          margin: '0 0 1.5rem 0',
          fontSize: '1.3rem',
          fontWeight: 600,
          color: '#238548',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem'
        }}>
          🔍 Filter Attendance Records
        </h3>

        {/* Filter Grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
          gap: '1rem',
          marginBottom: '1.5rem'
        }}>
          {attendanceType === 'regular' ? (
            <>
              {/* Department Filter */}
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '0.85rem',
                  fontWeight: 600,
                  color: '#374151',
                  marginBottom: '0.5rem'
                }}>
                  🏢 Department
                </label>
                <select
                  value={filterDepartment}
                  onChange={(e) => setFilterDepartment(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '2px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '0.95rem',
                    outline: 'none',
                    transition: 'border-color 0.3s ease',
                    backgroundColor: '#ffffff',
                    cursor: 'pointer'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#238548'}
                  onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                >
                  <option value="">Select Department</option>
                  {departments && departments.map((dept, index) => {
                    const deptValue = dept.dept_name || dept.value || dept;
                    const deptLabel = dept.dept_name || dept.label || dept;
                    return (
                      <option key={deptValue || index} value={deptValue}>
                        {deptLabel}
                      </option>
                    );
                  })}
                </select>
              </div>

              {/* Date Filter */}
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '0.85rem',
                  fontWeight: 600,
                  color: '#374151',
                  marginBottom: '0.5rem'
                }}>
                  📅 Date
                </label>
                <input
                  type="date"
                  value={filterDate}
                  onChange={(e) => setFilterDate(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '2px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '0.95rem',
                    outline: 'none',
                    transition: 'border-color 0.3s ease',
                    backgroundColor: '#ffffff'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#238548'}
                  onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                />
              </div>

              {/* Student ID Filter */}
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '0.85rem',
                  fontWeight: 600,
                  color: '#374151',
                  marginBottom: '0.5rem'
                }}>
                  👤 Student ID
                </label>
                <input
                  type="text"
                  placeholder="Search by Student ID..."
                  value={filterStudentId}
                  onChange={(e) => setFilterStudentId(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '2px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '0.95rem',
                    outline: 'none',
                    transition: 'border-color 0.3s ease',
                    backgroundColor: '#ffffff'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#238548'}
                  onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                />
              </div>

              {/* Status Filter */}
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '0.85rem',
                  fontWeight: 600,
                  color: '#374151',
                  marginBottom: '0.5rem'
                }}>
                  📊 Status
                </label>
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '2px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '0.95rem',
                    outline: 'none',
                    transition: 'border-color 0.3s ease',
                    backgroundColor: '#ffffff',
                    cursor: 'pointer'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#238548'}
                  onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                >
                  <option value="">All Status</option>
                  <option value="present">Present</option>
                  <option value="absent">Absent</option>
                </select>
              </div>

              {/* Subject Filter */}
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '0.85rem',
                  fontWeight: 600,
                  color: '#374151',
                  marginBottom: '0.5rem'
                }}>
                  📚 Subject
                </label>
                <input
                  type="text"
                  placeholder="Search by Subject..."
                  value={filterSubject}
                  onChange={(e) => setFilterSubject(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '2px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '0.95rem',
                    outline: 'none',
                    transition: 'border-color 0.3s ease',
                    backgroundColor: '#ffffff'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#238548'}
                  onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                />
              </div>

              {/* Batch Name Filter */}
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '0.85rem',
                  fontWeight: 600,
                  color: '#374151',
                  marginBottom: '0.5rem'
                }}>
                  🎓 Batch Name
                </label>
                <input
                  type="text"
                  placeholder="Search by Batch..."
                  value={filterBatchName}
                  onChange={(e) => setFilterBatchName(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '2px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '0.95rem',
                    outline: 'none',
                    transition: 'border-color 0.3s ease',
                    backgroundColor: '#ffffff'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#238548'}
                  onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                />
              </div>
            </>
          ) : (
            <>
              {/* Training Program Filter */}
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '0.85rem',
                  fontWeight: 600,
                  color: '#374151',
                  marginBottom: '0.5rem'
                }}>
                  💼 Training Program
                </label>
                <select
                  value={filterTrainingProgram}
                  onChange={(e) => setFilterTrainingProgram(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '2px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '0.95rem',
                    outline: 'none',
                    transition: 'border-color 0.3s ease',
                    backgroundColor: '#ffffff',
                    cursor: 'pointer'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#238548'}
                  onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                >
                  <option value="">Select Program</option>
                  {trainingPrograms.map((prog) => (
                    <option key={prog.id} value={prog.id}>
                      {prog.program_name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Training Batch Filter */}
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '0.85rem',
                  fontWeight: 600,
                  color: '#374151',
                  marginBottom: '0.5rem'
                }}>
                  🎓 Training Batch
                </label>
                <select
                  value={filterTrainingBatch}
                  onChange={(e) => setFilterTrainingBatch(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '2px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '0.95rem',
                    outline: 'none',
                    transition: 'border-color 0.3s ease',
                    backgroundColor: '#ffffff',
                    cursor: 'pointer'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#238548'}
                  onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                >
                  <option value="">Select Batch</option>
                  {trainingBatches.map((batch) => (
                    <option key={batch.id} value={batch.id}>
                      {batch.batch_name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Training Date Filter */}
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '0.85rem',
                  fontWeight: 600,
                  color: '#374151',
                  marginBottom: '0.5rem'
                }}>
                  📅 Date
                </label>
                <input
                  type="date"
                  value={filterTrainingDate}
                  onChange={(e) => setFilterTrainingDate(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '2px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '0.95rem',
                    outline: 'none',
                    transition: 'border-color 0.3s ease',
                    backgroundColor: '#ffffff'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#238548'}
                  onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                />
              </div>

              {/* Training Student ID Filter */}
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '0.85rem',
                  fontWeight: 600,
                  color: '#374151',
                  marginBottom: '0.5rem'
                }}>
                  👤 Student ID
                </label>
                <input
                  type="text"
                  placeholder="Search by Student ID..."
                  value={filterTrainingStudentId}
                  onChange={(e) => setFilterTrainingStudentId(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '2px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '0.95rem',
                    outline: 'none',
                    transition: 'border-color 0.3s ease',
                    backgroundColor: '#ffffff'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#238548'}
                  onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                />
              </div>

              {/* Training Status Filter */}
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '0.85rem',
                  fontWeight: 600,
                  color: '#374151',
                  marginBottom: '0.5rem'
                }}>
                  📊 Status
                </label>
                <select
                  value={filterTrainingStatus}
                  onChange={(e) => setFilterTrainingStatus(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '2px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '0.95rem',
                    outline: 'none',
                    transition: 'border-color 0.3s ease',
                    backgroundColor: '#ffffff',
                    cursor: 'pointer'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#238548'}
                  onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                >
                  <option value="">All Status</option>
                  <option value="present">Present</option>
                  <option value="absent">Absent</option>
                </select>
              </div>

              {/* Training Subject Filter */}
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '0.85rem',
                  fontWeight: 600,
                  color: '#374151',
                  marginBottom: '0.5rem'
                }}>
                  📚 Subject
                </label>
                <input
                  type="text"
                  placeholder="Search by Subject..."
                  value={filterTrainingSubject}
                  onChange={(e) => setFilterTrainingSubject(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '2px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '0.95rem',
                    outline: 'none',
                    transition: 'border-color 0.3s ease',
                    backgroundColor: '#ffffff'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#238548'}
                  onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                />
              </div>
            </>
          )}
        </div>

        {/* Action Buttons */}
        <div style={{
          display: 'flex',
          gap: '1rem',
          flexWrap: 'wrap'
        }}>
          <button
            onClick={handleClearFilters}
            style={{
              padding: '0.75rem 1.5rem',
              backgroundColor: '#f3f4f6',
              color: '#374151',
              border: '2px solid #e5e7eb',
              borderRadius: '8px',
              fontSize: '0.95rem',
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = '#e5e7eb';
              e.target.style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = '#f3f4f6';
              e.target.style.transform = 'translateY(0)';
            }}
          >
            🗑️ Clear All Filters
          </button>
        </div>

        {/* Filter Warning */}
        {!hasActiveFilters && (
          <div style={{
            marginTop: '1rem',
            padding: '1rem',
            backgroundColor: '#fef3c7',
            border: '2px solid #fcd34d',
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem'
          }}>
            <span style={{ fontSize: '1.5rem' }}>⚠️</span>
            <span style={{ color: '#92400e', fontSize: '0.95rem' }}>
              Please apply at least one filter to view {attendanceType === 'regular' ? 'regular' : 'training'} attendance data.
            </span>
          </div>
        )}
      </div>


      {/* Search and Controls Section */}
      <div style={{
        background: '#ffffff',
        padding: '2rem',
        borderRadius: '12px',
        marginBottom: '2rem',
        boxShadow: '0 4px 15px rgba(0,0,0,0.1)'
      }}>
        <div style={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: isMobile ? 'stretch' : 'center',
          gap: '1rem',
          marginBottom: '1rem'
        }}>
          {/* Search Bar */}
          <div style={{ flex: 1, maxWidth: isMobile ? '100%' : '400px' }}>
            <div style={{ position: 'relative' }}>
              <input
                type="text"
                placeholder={attendanceType === 'regular'
                  ? "Search by student ID, subject, batch, or department..."
                  : "Search by student ID, program, subject, or instructor..."
                }
                value={attendanceType === 'regular' ? searchTerm : trainingSearchTerm}
                onChange={(e) => {
                  const value = e.target.value;
                  console.log(`🔍 Search term changed: ${value}`);
                  if (attendanceType === 'regular') {
                    setSearchTerm(value);
                  } else {
                    setTrainingSearchTerm(value);
                  }
                }}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                style={{
                  width: '100%',
                  padding: '1rem 3rem 1rem 1rem',
                  border: '2px solid #e2e8f0',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  outline: 'none',
                  transition: 'all 0.3s ease'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#238548';
                  e.target.style.boxShadow = '0 0 0 3px rgba(35, 133, 72, 0.1)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#e2e8f0';
                  e.target.style.boxShadow = 'none';
                }}
              />
              <button
                onClick={handleSearch}
                style={{
                  position: 'absolute',
                  right: '10px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  fontSize: '1.2rem',
                  cursor: 'pointer',
                  color: '#666',
                  padding: '0.5rem'
                }}
              >
                🔍
              </button>
            </div>
          </div>

          {/* Add Record Button */}
          <button
            onClick={() => {
              resetForm();
              setShowAddModal(true);
            }}
            style={{
              padding: '1rem 2rem',
              background: 'linear-gradient(135deg, #238548 0%, #97d6ac 100%)',
              color: '#ffffff',
              border: 'none',
              borderRadius: '8px',
              fontSize: '1rem',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              whiteSpace: 'nowrap'
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = '0 8px 25px rgba(35, 133, 72, 0.3)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = 'none';
            }}
          >
            <span style={{ fontSize: '1.2rem' }}>+</span>
            Add {attendanceType === 'regular' ? 'Attendance' : 'Training'} Record
          </button>

          {/* Refresh Button */}
          <button
            onClick={refreshRecords}
            disabled={currentLoading}
            style={{
              padding: '1rem',
              background: '#f0f9ff',
              color: '#0369a1',
              border: '2px solid #e0f2fe',
              borderRadius: '8px',
              fontSize: '1rem',
              cursor: currentLoading ? 'not-allowed' : 'pointer',
              transition: 'all 0.3s ease',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              opacity: currentLoading ? 0.6 : 1
            }}
            onMouseEnter={(e) => {
              if (!currentLoading) {
                e.target.style.backgroundColor = '#0369a1';
                e.target.style.color = '#ffffff';
              }
            }}
            onMouseLeave={(e) => {
              if (!currentLoading) {
                e.target.style.backgroundColor = '#f0f9ff';
                e.target.style.color = '#0369a1';
              }
            }}
            title={`Refresh ${attendanceType} attendance records`}
          >
            {currentLoading ? (
              <div style={{
                width: '16px',
                height: '16px',
                border: '2px solid #0369a1',
                borderTop: '2px solid transparent',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }}></div>
            ) : (
              '🔄'
            )}
          </button>

          {/* ADDED: Force Load Training Data Button */}
          {attendanceType === 'training' && (
            <button
              onClick={() => {
                console.log('🔄 Force loading ALL training data...');
                setInitialTrainingLoad(false);
                fetchAllTrainingRecords();
                fetchTrainingFilterOptions();
              }}
              disabled={trainingLoading}
              style={{
                padding: '1rem',
                background: '#fef3c7',
                color: '#d97706',
                border: '2px solid #fbbf24',
                borderRadius: '8px',
                fontSize: '1rem',
                cursor: trainingLoading ? 'not-allowed' : 'pointer',
                transition: 'all 0.3s ease',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                opacity: trainingLoading ? 0.6 : 1,
                whiteSpace: 'nowrap'
              }}
              onMouseEnter={(e) => {
                if (!trainingLoading) {
                  e.target.style.backgroundColor = '#d97706';
                  e.target.style.color = '#ffffff';
                }
              }}
              onMouseLeave={(e) => {
                if (!trainingLoading) {
                  e.target.style.backgroundColor = '#fef3c7';
                  e.target.style.color = '#d97706';
                }
              }}
              title="Force reload ALL training data and filter options"
            >
              {trainingLoading ? '⏳' : '🚀'} Load ALL Training
            </button>
          )}
        </div>

        {/* Stats */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          paddingTop: '1rem',
          borderTop: '1px solid #ecf0f1',
          fontSize: '0.9rem',
          color: '#666'
        }}>
          <span>
            {loading ? `Loading filtered attendance records...` :
              hasActiveFilters ? `Showing ${currentDisplayedRecords.length} of ${totalCount.toLocaleString()} filtered records (from ${allRecordsCount.toLocaleString()} total in database)` :
                `${allRecordsCount.toLocaleString()} total records in database - Apply filters to view data`}
          </span>
          {currentLoading && (
            <span style={{ color: '#238548' }}>
              <div style={{
                width: '16px',
                height: '16px',
                border: '2px solid #e3f2fd',
                borderTop: '2px solid #238548',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                display: 'inline-block'
              }}></div>
            </span>
          )}
        </div>
      </div>

      {/* Attendance Records Table */}
      <div style={{
        background: '#ffffff',
        borderRadius: '12px',
        boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
        overflow: 'hidden'
      }}>
        {currentLoading && currentRecords.length === 0 ? (
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '4rem',
            flexDirection: 'column',
            gap: '1rem'
          }}>
            <div style={{
              width: '50px',
              height: '50px',
              border: '4px solid #e3f2fd',
              borderTop: '4px solid #238548',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }}></div>
            <p style={{ color: '#666', fontSize: '1.1rem' }}>Loading ALL {attendanceType} attendance records from database...</p>
            <p style={{ color: '#999', fontSize: '0.9rem' }}>This may take a moment as we're fetching all records without limits</p>
          </div>
        ) : currentDisplayedRecords.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: '4rem 2rem'
          }}>
            <div style={{ fontSize: '4rem', marginBottom: '1rem', opacity: 0.5 }}>
              📊
            </div>
            <h3 style={{
              fontSize: '1.5rem',
              fontWeight: 600,
              color: '#333',
              margin: '0 0 1rem 0'
            }}>
              {allRecordsCount > 0 ? `Database contains ${allRecordsCount.toLocaleString()} total attendance records` : 'No attendance records found'}
            </h3>
            <p style={{ color: '#666', margin: '0 0 2rem 0', fontSize: '1rem' }}>
              {hasActiveFilters
                ? 'No records match your current filters. Try adjusting your search criteria.'
                : 'Apply filters above to view specific attendance data'
              }
            </p>

            {!hasActiveFilters && allRecordsCount > 0 && (
              <div style={{
                padding: '2rem',
                backgroundColor: '#e0f2fe',
                borderRadius: '12px',
                margin: '0 auto',
                maxWidth: '600px',
                border: '2px solid #81d4fa'
              }}>
                <h4 style={{ margin: '0 0 1rem 0', color: '#0277bd', fontSize: '1.2rem' }}>
                  🎯 Smart Loading System
                </h4>
                <p style={{ margin: '0 0 1rem 0', color: '#01579b', fontSize: '1rem' }}>
                  <strong>Total Records:</strong> {allRecordsCount.toLocaleString()} attendance entries
                </p>
                <p style={{ margin: 0, color: '#0277bd', fontSize: '0.9rem' }}>
                  To ensure fast loading, please select filters above to view specific data.
                  This prevents loading all {allRecordsCount.toLocaleString()} records at once.
                </p>
              </div>
            )}
          </div>
        ) : (
          <>
            <div style={{ overflowX: 'auto' }}>
              <table style={{
                width: '100%',
                borderCollapse: 'collapse',
                fontSize: isMobile ? '0.85rem' : '0.95rem'
              }}>
                <thead>
                  <tr style={{
                    background: 'linear-gradient(135deg, #238548 0%, #97d6ac 100%)',
                    color: '#ffffff'
                  }}>
                    <th style={{
                      padding: '1rem',
                      textAlign: 'left',
                      fontWeight: 600,
                      borderBottom: 'none',
                      whiteSpace: 'nowrap'
                    }}>Date</th>
                    <th style={{
                      padding: '1rem',
                      textAlign: 'left',
                      fontWeight: 600,
                      borderBottom: 'none',
                      whiteSpace: 'nowrap'
                    }}>Student</th>
                    {attendanceType === 'regular' ? (
                      <>
                        <th style={{
                          padding: '1rem',
                          textAlign: 'left',
                          fontWeight: 600,
                          borderBottom: 'none',
                          whiteSpace: 'nowrap'
                        }}>Department</th>
                        <th style={{
                          padding: '1rem',
                          textAlign: 'left',
                          fontWeight: 600,
                          borderBottom: 'none',
                          whiteSpace: 'nowrap'
                        }}>Year/Batch</th>
                        <th style={{
                          padding: '1rem',
                          textAlign: 'left',
                          fontWeight: 600,
                          borderBottom: 'none',
                          whiteSpace: 'nowrap'
                        }}>Subject</th>
                      </>
                    ) : (
                      <>
                        <th style={{
                          padding: '1rem',
                          textAlign: 'left',
                          fontWeight: 600,
                          borderBottom: 'none',
                          whiteSpace: 'nowrap'
                        }}>Training Program</th>
                        <th style={{
                          padding: '1rem',
                          textAlign: 'left',
                          fontWeight: 600,
                          borderBottom: 'none',
                          whiteSpace: 'nowrap'
                        }}>Batch</th>
                        <th style={{
                          padding: '1rem',
                          textAlign: 'left',
                          fontWeight: 600,
                          borderBottom: 'none',
                          whiteSpace: 'nowrap'
                        }}>Subject</th>
                        <th style={{
                          padding: '1rem',
                          textAlign: 'left',
                          fontWeight: 600,
                          borderBottom: 'none',
                          whiteSpace: 'nowrap'
                        }}>Instructor</th>
                      </>
                    )}
                    <th style={{
                      padding: '1rem',
                      textAlign: 'center',
                      fontWeight: 600,
                      borderBottom: 'none',
                      whiteSpace: 'nowrap'
                    }}>Status</th>
                    {attendanceType === 'training' && (
                      <th style={{
                        padding: '1rem',
                        textAlign: 'center',
                        fontWeight: 600,
                        borderBottom: 'none',
                        whiteSpace: 'nowrap'
                      }}>Hours</th>
                    )}
                    <th style={{
                      padding: '1rem',
                      textAlign: 'center',
                      fontWeight: 600,
                      borderBottom: 'none',
                      whiteSpace: 'nowrap'
                    }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {currentDisplayedRecords.map((record) => (
                    <tr key={record.id} style={{
                      borderBottom: '1px solid #f1f5f9',
                      transition: 'background-color 0.3s ease'
                    }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8fafc'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                    >
                      <td style={{ padding: '1rem', fontWeight: 500 }}>
                        {formatDate(record.date)}
                      </td>
                      <td style={{ padding: '1rem' }}>
                        <div>
                          <div style={{ fontWeight: 500, color: '#238548', marginBottom: '0.25rem' }}>
                            {record.student_id}
                          </div>
                          {attendanceType === 'training' && record.student_name && (
                            <div style={{ fontSize: '0.8rem', color: '#666' }}>
                              {record.student_name}
                            </div>
                          )}
                        </div>
                      </td>
                      {attendanceType === 'regular' ? (
                        <>
                          <td style={{ padding: '1rem', color: '#333' }}>
                            <span style={{
                              padding: '0.25rem 0.75rem',
                              backgroundColor: '#e8f5e9',
                              color: '#2e7d32',
                              borderRadius: '20px',
                              fontSize: '0.8rem',
                              fontWeight: 500,
                              textTransform: 'uppercase'
                            }}>
                              {record.department}
                            </span>
                          </td>
                          <td style={{ padding: '1rem', color: '#333' }}>
                            <div>
                              <div style={{ fontWeight: 500, marginBottom: '0.25rem' }}>
                                Year {record.year}
                              </div>
                              <div style={{ fontSize: '0.8rem', color: '#666' }}>
                                {record.batch_name}
                              </div>
                            </div>
                          </td>
                          <td style={{ padding: '1rem', color: '#333' }}>
                            {record.subject}
                          </td>
                        </>
                      ) : (
                        <>
                          <td style={{ padding: '1rem', color: '#333' }}>
                            <span style={{
                              padding: '0.25rem 0.75rem',
                              backgroundColor: '#e8f5e9',
                              color: '#2e7d32',
                              borderRadius: '20px',
                              fontSize: '0.8rem',
                              fontWeight: 500,
                              textTransform: 'uppercase'
                            }}>
                              {record.program_name}
                            </span>
                          </td>
                          <td style={{ padding: '1rem', color: '#333' }}>
                            <div>
                              <div style={{ fontWeight: 500, marginBottom: '0.25rem' }}>
                                {record.training_batch_name}
                              </div>
                              {record.location && (
                                <div style={{ fontSize: '0.8rem', color: '#666' }}>
                                  📍 {record.location}
                                </div>
                              )}
                            </div>
                          </td>
                          <td style={{ padding: '1rem', color: '#333' }}>
                            <div>
                              <div style={{ fontWeight: 500, marginBottom: '0.25rem' }}>
                                {record.subject_code}
                              </div>
                              <div style={{ fontSize: '0.8rem', color: '#666' }}>
                                {record.subject_name}
                              </div>
                            </div>
                          </td>
                          <td style={{ padding: '1rem', color: '#333', fontSize: '0.9rem' }}>
                            {record.instructor_name}
                          </td>
                        </>
                      )}
                      <td style={{ padding: '1rem', textAlign: 'center' }}>
                        <span style={{
                          padding: '0.5rem 1rem',
                          borderRadius: '20px',
                          fontSize: '0.8rem',
                          fontWeight: 600,
                          textTransform: 'uppercase',
                          backgroundColor: safeToLowerCase(record.status) === 'present' ? '#e8f5e9' : '#ffebee',
                          color: safeToLowerCase(record.status) === 'present' ? '#2e7d32' : '#d32f2f'
                        }}>
                          {safeToLowerCase(record.status) === 'present' ? '✔ Present' : '✗ Absent'}
                        </span>
                      </td>
                      {attendanceType === 'training' && (
                        <td style={{ padding: '1rem', textAlign: 'center', fontSize: '0.9rem', fontWeight: 500 }}>
                          {record.session_hours || 1.0}h
                        </td>
                      )}
                      <td style={{ padding: '1rem', textAlign: 'center' }}>
                        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center' }}>
                          <button
                            onClick={() => handleEditClick(record)}
                            style={{
                              padding: '0.5rem 1rem',
                              backgroundColor: '#e3f2fd',
                              color: '#1976d2',
                              border: 'none',
                              borderRadius: '6px',
                              fontSize: '0.85rem',
                              cursor: 'pointer',
                              transition: 'all 0.3s ease',
                              fontWeight: 500
                            }}
                            onMouseEnter={(e) => {
                              e.target.style.backgroundColor = '#1976d2';
                              e.target.style.color = '#ffffff';
                              e.target.style.transform = 'translateY(-1px)';
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.backgroundColor = '#e3f2fd';
                              e.target.style.color = '#1976d2';
                              e.target.style.transform = 'translateY(0)';
                            }}
                          >
                            ✏️ Edit
                          </button>
                          <button
                            onClick={() => handleDeleteClick(record)}
                            style={{
                              padding: '0.5rem 1rem',
                              backgroundColor: '#ffebee',
                              color: '#d32f2f',
                              border: 'none',
                              borderRadius: '6px',
                              fontSize: '0.85rem',
                              cursor: 'pointer',
                              transition: 'all 0.3s ease',
                              fontWeight: 500
                            }}
                            onMouseEnter={(e) => {
                              e.target.style.backgroundColor = '#d32f2f';
                              e.target.style.color = '#ffffff';
                              e.target.style.transform = 'translateY(-1px)';
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.backgroundColor = '#ffebee';
                              e.target.style.color = '#d32f2f';
                              e.target.style.transform = 'translateY(0)';
                            }}
                          >
                            🗑️ Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div style={{
                padding: '1.5rem',
                borderTop: '1px solid #f1f5f9',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: '1rem'
              }}>
                <div style={{ color: '#666', fontSize: '0.9rem' }}>
                  Page {currentPage} of {totalPages} • {totalCount.toLocaleString()} total records (filtered from {currentRecords.length.toLocaleString()})
                </div>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    style={{
                      padding: '0.5rem 1rem',
                      border: '2px solid #e2e8f0',
                      backgroundColor: currentPage === 1 ? '#f8fafc' : '#ffffff',
                      color: currentPage === 1 ? '#9ca3af' : '#374151',
                      borderRadius: '6px',
                      cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                      transition: 'all 0.3s ease',
                      fontSize: '0.9rem'
                    }}
                    onMouseEnter={(e) => {
                      if (currentPage !== 1) {
                        e.target.style.borderColor = '#238548';
                        e.target.style.backgroundColor = '#f0fdf4';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (currentPage !== 1) {
                        e.target.style.borderColor = '#e2e8f0';
                        e.target.style.backgroundColor = '#ffffff';
                      }
                    }}
                  >
                    ← Previous
                  </button>

                  {[...Array(Math.min(5, totalPages))].map((_, index) => {
                    let page;
                    if (totalPages <= 5) {
                      page = index + 1;
                    } else if (currentPage <= 3) {
                      page = index + 1;
                    } else if (currentPage >= totalPages - 2) {
                      page = totalPages - 4 + index;
                    } else {
                      page = currentPage - 2 + index;
                    }

                    return (
                      <button
                        key={page}
                        onClick={() => handlePageChange(page)}
                        style={{
                          padding: '0.5rem 0.75rem',
                          border: '2px solid',
                          borderColor: page === currentPage ? '#238548' : '#e2e8f0',
                          backgroundColor: page === currentPage ? '#238548' : '#ffffff',
                          color: page === currentPage ? '#ffffff' : '#374151',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          transition: 'all 0.3s ease',
                          fontSize: '0.9rem',
                          fontWeight: page === currentPage ? '600' : '400'
                        }}
                        onMouseEnter={(e) => {
                          if (page !== currentPage) {
                            e.target.style.borderColor = '#238548';
                            e.target.style.backgroundColor = '#f0fdf4';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (page !== currentPage) {
                            e.target.style.borderColor = '#e2e8f0';
                            e.target.style.backgroundColor = '#ffffff';
                          }
                        }}
                      >
                        {page}
                      </button>
                    );
                  })}

                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    style={{
                      padding: '0.5rem 1rem',
                      border: '2px solid #e2e8f0',
                      backgroundColor: currentPage === totalPages ? '#f8fafc' : '#ffffff',
                      color: currentPage === totalPages ? '#9ca3af' : '#374151',
                      borderRadius: '6px',
                      cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                      transition: 'all 0.3s ease',
                      fontSize: '0.9rem'
                    }}
                    onMouseEnter={(e) => {
                      if (currentPage !== totalPages) {
                        e.target.style.borderColor = '#238548';
                        e.target.style.backgroundColor = '#f0fdf4';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (currentPage !== totalPages) {
                        e.target.style.borderColor = '#e2e8f0';
                        e.target.style.backgroundColor = '#ffffff';
                      }
                    }}
                  >
                    Next →
                  </button>
                </div>
              </div>
            )}
          </>
        )}

        {/* ========================================== */}
        {/* NEW: SUBJECT-WISE ATTENDANCE ANALYTICS */}
        {/* ========================================== */}
        <div style={{
          marginTop: '2rem',
          padding: '2rem',
          backgroundColor: '#ffffff',
          borderRadius: '12px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
        }}>
          {/* Section Header */}
          <div style={{
            marginBottom: '2rem',
            borderBottom: '2px solid #238548',
            paddingBottom: '1rem'
          }}>
            <h2 style={{
              margin: 0,
              fontSize: '1.5rem',
              fontWeight: 600,
              color: '#238548',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              📊 Subject-Wise Attendance Analytics
            </h2>
            <p style={{
              margin: '0.5rem 0 0 0',
              fontSize: '0.9rem',
              color: '#666'
            }}>
              Filter by Department → Year → Batch → Subject to view attendance analytics
            </p>
          </div>

          {/* Filter Dropdowns */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
            gap: '1.5rem',
            marginBottom: '2rem'
          }}>
            {/* Department Dropdown */}
            <div>
              <label style={{
                display: 'block',
                fontSize: '0.9rem',
                fontWeight: 600,
                color: '#374151',
                marginBottom: '0.5rem'
              }}>
                Department *
              </label>
              <select
                value={subjectAnalyticsDept}
                onChange={(e) => handleSubjectAnalyticsDeptChange(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '2px solid #e5e7eb',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  outline: 'none',
                  transition: 'border-color 0.3s ease',
                  backgroundColor: '#ffffff',
                  cursor: 'pointer'
                }}
                onFocus={(e) => e.target.style.borderColor = '#238548'}
                onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
              >
                <option value="">Select Department</option>
                {departments && departments.length > 0 ? (
                  departments.map((dept, index) => {
                    // Handle both formats: {dept_name: 'X'} or {value: 'X', label: 'X'}
                    const deptValue = dept.dept_name || dept.value || dept;
                    const deptLabel = dept.dept_name || dept.label || dept;
                    return (
                      <option key={deptValue || index} value={deptValue}>
                        {deptLabel}
                      </option>
                    );
                  })
                ) : (
                  <option value="" disabled>No departments available</option>
                )}
              </select>
            </div>

            {/* Year Dropdown */}
            <div>
              <label style={{
                display: 'block',
                fontSize: '0.9rem',
                fontWeight: 600,
                color: '#374151',
                marginBottom: '0.5rem'
              }}>
                Year *
              </label>
              <select
                value={subjectAnalyticsYear}
                onChange={(e) => handleSubjectAnalyticsYearChange(e.target.value)}
                disabled={!subjectAnalyticsDept}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '2px solid #e5e7eb',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  outline: 'none',
                  transition: 'border-color 0.3s ease',
                  backgroundColor: !subjectAnalyticsDept ? '#f9fafb' : '#ffffff',
                  cursor: !subjectAnalyticsDept ? 'not-allowed' : 'pointer',
                  opacity: !subjectAnalyticsDept ? 0.6 : 1
                }}
                onFocus={(e) => e.target.style.borderColor = '#238548'}
                onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
              >
                <option value="">Select Year</option>
                <option value="1">1st Year</option>
                <option value="2">2nd Year</option>
                <option value="3">3rd Year</option>
                <option value="4">4th Year</option>
              </select>
            </div>

            {/* Batch Dropdown */}
            <div>
              <label style={{
                display: 'block',
                fontSize: '0.9rem',
                fontWeight: 600,
                color: '#374151',
                marginBottom: '0.5rem'
              }}>
                Batch *
              </label>
              <select
                value={subjectAnalyticsBatch}
                onChange={(e) => handleSubjectAnalyticsBatchChange(e.target.value)}
                disabled={!subjectAnalyticsYear || subjectAnalyticsBatches.length === 0}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '2px solid #e5e7eb',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  outline: 'none',
                  transition: 'border-color 0.3s ease',
                  backgroundColor: (!subjectAnalyticsYear || subjectAnalyticsBatches.length === 0) ? '#f9fafb' : '#ffffff',
                  cursor: (!subjectAnalyticsYear || subjectAnalyticsBatches.length === 0) ? 'not-allowed' : 'pointer',
                  opacity: (!subjectAnalyticsYear || subjectAnalyticsBatches.length === 0) ? 0.6 : 1
                }}
                onFocus={(e) => e.target.style.borderColor = '#238548'}
                onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
              >
                <option value="">Select Batch</option>
                {subjectAnalyticsBatches.map((batch) => (
                  <option key={batch} value={batch}>
                    {batch}
                  </option>
                ))}
              </select>
            </div>

            {/* Subject Dropdown */}
            <div>
              <label style={{
                display: 'block',
                fontSize: '0.9rem',
                fontWeight: 600,
                color: '#374151',
                marginBottom: '0.5rem'
              }}>
                Subject *
              </label>
              <select
                value={subjectAnalyticsSubject}
                onChange={(e) => handleSubjectAnalyticsSubjectChange(e.target.value)}
                disabled={!subjectAnalyticsBatch || subjectAnalyticsSubjects.length === 0}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '2px solid #e5e7eb',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  outline: 'none',
                  transition: 'border-color 0.3s ease',
                  backgroundColor: (!subjectAnalyticsBatch || subjectAnalyticsSubjects.length === 0) ? '#f9fafb' : '#ffffff',
                  cursor: (!subjectAnalyticsBatch || subjectAnalyticsSubjects.length === 0) ? 'not-allowed' : 'pointer',
                  opacity: (!subjectAnalyticsBatch || subjectAnalyticsSubjects.length === 0) ? 0.6 : 1
                }}
                onFocus={(e) => e.target.style.borderColor = '#238548'}
                onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
              >
                <option value="">Select Subject</option>
                {subjectAnalyticsSubjects.map((subject) => (
                  <option key={subject.value} value={subject.value}>
                    {subject.label}
                  </option>
                ))}
              </select>
            </div>

            {/* From Date Filter */}
            <div>
              <label style={{
                display: 'block',
                fontSize: '0.9rem',
                fontWeight: 600,
                color: '#374151',
                marginBottom: '0.5rem'
              }}>
                From Date (Optional)
              </label>
              <input
                type="date"
                value={subjectAnalyticsFromDate}
                onChange={(e) => setSubjectAnalyticsFromDate(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '2px solid #e5e7eb',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  outline: 'none',
                  transition: 'border-color 0.3s ease',
                  backgroundColor: '#ffffff'
                }}
                onFocus={(e) => e.target.style.borderColor = '#238548'}
                onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
              />
            </div>

            {/* To Date Filter */}
            <div>
              <label style={{
                display: 'block',
                fontSize: '0.9rem',
                fontWeight: 600,
                color: '#374151',
                marginBottom: '0.5rem'
              }}>
                To Date (Optional)
              </label>
              <input
                type="date"
                value={subjectAnalyticsToDate}
                onChange={(e) => setSubjectAnalyticsToDate(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '2px solid #e5e7eb',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  outline: 'none',
                  transition: 'border-color 0.3s ease',
                  backgroundColor: '#ffffff'
                }}
                onFocus={(e) => e.target.style.borderColor = '#238548'}
                onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
              />
            </div>
          </div>

          {/* Loading State */}
          {subjectAnalyticsLoading && (
            <div style={{
              padding: '3rem',
              textAlign: 'center',
              color: '#666'
            }}>
              <div style={{
                width: '40px',
                height: '40px',
                border: '4px solid #f3f4f6',
                borderTop: '4px solid #238548',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                margin: '0 auto 1rem'
              }}></div>
              <p style={{ margin: 0, fontSize: '1rem' }}>Loading analytics...</p>
            </div>
          )}

          {/* Analytics Display */}
          {!subjectAnalyticsLoading && subjectAnalyticsData && (
            <div>
              {/* Summary Header */}
              <div style={{
                padding: '1.5rem',
                backgroundColor: '#f0fdf4',
                borderRadius: '8px',
                marginBottom: '2rem',
                border: '1px solid #bbf7d0'
              }}>
                <h3 style={{
                  margin: '0 0 0.5rem 0',
                  fontSize: '1.2rem',
                  fontWeight: 600,
                  color: '#238548'
                }}>
                  Analytics Summary
                </h3>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
                  gap: '1rem',
                  marginTop: '1rem'
                }}>
                  <div>
                    <p style={{ margin: 0, fontSize: '0.85rem', color: '#666' }}>Department</p>
                    <p style={{ margin: '0.25rem 0 0 0', fontSize: '1rem', fontWeight: 600, color: '#238548' }}>
                      {subjectAnalyticsData.department}
                    </p>
                  </div>
                  <div>
                    <p style={{ margin: 0, fontSize: '0.85rem', color: '#666' }}>Subject</p>
                    <p style={{ margin: '0.25rem 0 0 0', fontSize: '1rem', fontWeight: 600, color: '#238548' }}>
                      {subjectAnalyticsData.subject}
                    </p>
                  </div>
                  <div>
                    <p style={{ margin: 0, fontSize: '0.85rem', color: '#666' }}>Total Students</p>
                    <p style={{ margin: '0.25rem 0 0 0', fontSize: '1rem', fontWeight: 600, color: '#238548' }}>
                      {subjectAnalyticsData.totalStudents}
                    </p>
                  </div>
                </div>
              </div>

              {/* Analytics Cards */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
                gap: '1.5rem'
              }}>
                {/* Below 50% Card */}
                <div style={{
                  padding: '1.5rem',
                  background: 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)',
                  borderRadius: '12px',
                  border: '2px solid #fca5a5',
                  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                  transition: 'transform 0.3s ease, box-shadow 0.3s ease'
                }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-4px)';
                    e.currentTarget.style.boxShadow = '0 8px 12px rgba(0, 0, 0, 0.15)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.1)';
                  }}
                >
                  <h4 style={{
                    margin: '0 0 1rem 0',
                    fontSize: '1.1rem',
                    fontWeight: 600,
                    color: '#dc2626',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}>
                    ⚠️ Below 50% Attendance
                  </h4>
                  <div style={{
                    fontSize: '2.5rem',
                    fontWeight: 700,
                    color: '#dc2626',
                    marginBottom: '0.5rem'
                  }}>
                    {subjectAnalyticsData.categories.below50?.count || 0}
                  </div>
                  <p style={{
                    margin: '0 0 1rem 0',
                    fontSize: '0.9rem',
                    color: '#991b1b'
                  }}>
                    {subjectAnalyticsData.categories.below50?.percentage || 0}% of total students
                  </p>
                  <button
                    onClick={() => downloadSubjectAnalyticsCategory('below50', 'Below 50%')}
                    disabled={!subjectAnalyticsData.categories.below50?.count}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      backgroundColor: !subjectAnalyticsData.categories.below50?.count ? '#fca5a5' : '#dc2626',
                      color: '#ffffff',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '0.9rem',
                      fontWeight: 600,
                      cursor: !subjectAnalyticsData.categories.below50?.count ? 'not-allowed' : 'pointer',
                      transition: 'all 0.3s ease',
                      opacity: !subjectAnalyticsData.categories.below50?.count ? 0.5 : 1
                    }}
                    onMouseEnter={(e) => {
                      if (subjectAnalyticsData.categories.below50?.count) {
                        e.target.style.backgroundColor = '#b91c1c';
                        e.target.style.transform = 'translateY(-2px)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (subjectAnalyticsData.categories.below50?.count) {
                        e.target.style.backgroundColor = '#dc2626';
                        e.target.style.transform = 'translateY(0)';
                      }
                    }}
                  >
                    📥 Download Report
                  </button>
                </div>

                {/* 50-75% Card */}
                <div style={{
                  padding: '1.5rem',
                  background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                  borderRadius: '12px',
                  border: '2px solid #fcd34d',
                  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                  transition: 'transform 0.3s ease, box-shadow 0.3s ease'
                }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-4px)';
                    e.currentTarget.style.boxShadow = '0 8px 12px rgba(0, 0, 0, 0.15)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.1)';
                  }}
                >
                  <h4 style={{
                    margin: '0 0 1rem 0',
                    fontSize: '1.1rem',
                    fontWeight: 600,
                    color: '#d97706',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}>
                    ⚡ 50-75% Attendance
                  </h4>
                  <div style={{
                    fontSize: '2.5rem',
                    fontWeight: 700,
                    color: '#d97706',
                    marginBottom: '0.5rem'
                  }}>
                    {subjectAnalyticsData.categories.between50And75?.count || 0}
                  </div>
                  <p style={{
                    margin: '0 0 1rem 0',
                    fontSize: '0.9rem',
                    color: '#92400e'
                  }}>
                    {subjectAnalyticsData.categories.between50And75?.percentage || 0}% of total students
                  </p>
                  <button
                    onClick={() => downloadSubjectAnalyticsCategory('between50And75', '50-75%')}
                    disabled={!subjectAnalyticsData.categories.between50And75?.count}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      backgroundColor: !subjectAnalyticsData.categories.between50And75?.count ? '#fcd34d' : '#d97706',
                      color: '#ffffff',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '0.9rem',
                      fontWeight: 600,
                      cursor: !subjectAnalyticsData.categories.between50And75?.count ? 'not-allowed' : 'pointer',
                      transition: 'all 0.3s ease',
                      opacity: !subjectAnalyticsData.categories.between50And75?.count ? 0.5 : 1
                    }}
                    onMouseEnter={(e) => {
                      if (subjectAnalyticsData.categories.between50And75?.count) {
                        e.target.style.backgroundColor = '#b45309';
                        e.target.style.transform = 'translateY(-2px)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (subjectAnalyticsData.categories.between50And75?.count) {
                        e.target.style.backgroundColor = '#d97706';
                        e.target.style.transform = 'translateY(0)';
                      }
                    }}
                  >
                    📥 Download Report
                  </button>
                </div>

                {/* Above 90% Card */}
                <div style={{
                  padding: '1.5rem',
                  background: 'linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)',
                  borderRadius: '12px',
                  border: '2px solid #6ee7b7',
                  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                  transition: 'transform 0.3s ease, box-shadow 0.3s ease'
                }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-4px)';
                    e.currentTarget.style.boxShadow = '0 8px 12px rgba(0, 0, 0, 0.15)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.1)';
                  }}
                >
                  <h4 style={{
                    margin: '0 0 1rem 0',
                    fontSize: '1.1rem',
                    fontWeight: 600,
                    color: '#059669',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}>
                    ✅ Above 90% Attendance
                  </h4>
                  <div style={{
                    fontSize: '2.5rem',
                    fontWeight: 700,
                    color: '#059669',
                    marginBottom: '0.5rem'
                  }}>
                    {subjectAnalyticsData.categories.above90?.count || 0}
                  </div>
                  <p style={{
                    margin: '0 0 1rem 0',
                    fontSize: '0.9rem',
                    color: '#065f46'
                  }}>
                    {subjectAnalyticsData.categories.above90?.percentage || 0}% of total students
                  </p>
                  <button
                    onClick={() => downloadSubjectAnalyticsCategory('above90', 'Above 90%')}
                    disabled={!subjectAnalyticsData.categories.above90?.count}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      backgroundColor: !subjectAnalyticsData.categories.above90?.count ? '#6ee7b7' : '#059669',
                      color: '#ffffff',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '0.9rem',
                      fontWeight: 600,
                      cursor: !subjectAnalyticsData.categories.above90?.count ? 'not-allowed' : 'pointer',
                      transition: 'all 0.3s ease',
                      opacity: !subjectAnalyticsData.categories.above90?.count ? 0.5 : 1
                    }}
                    onMouseEnter={(e) => {
                      if (subjectAnalyticsData.categories.above90?.count) {
                        e.target.style.backgroundColor = '#047857';
                        e.target.style.transform = 'translateY(-2px)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (subjectAnalyticsData.categories.above90?.count) {
                        e.target.style.backgroundColor = '#059669';
                        e.target.style.transform = 'translateY(0)';
                      }
                    }}
                  >
                    📥 Download Report
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Empty State */}
          {!subjectAnalyticsLoading && !subjectAnalyticsData && (
            <div style={{
              padding: '3rem',
              textAlign: 'center',
              color: '#9ca3af'
            }}>
              <div style={{
                fontSize: '4rem',
                marginBottom: '1rem'
              }}>
                📊
              </div>
              <p style={{
                margin: 0,
                fontSize: '1.1rem',
                fontWeight: 500,
                color: '#6b7280'
              }}>
                Select Department, Year, Batch, and Subject to view analytics
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Add/Edit Modal */}
      {(showAddModal || showEditModal) && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000,
          padding: '1rem'
        }}>
          <div style={{
            background: '#ffffff',
            borderRadius: '12px',
            boxShadow: '0 25px 50px rgba(0, 0, 0, 0.25)',
            width: '100%',
            maxWidth: '700px',
            maxHeight: '90vh',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column'
          }}>
            {/* Modal Header */}
            <div style={{
              background: 'linear-gradient(135deg, #238548 0%, #97d6ac 100%)',
              color: '#ffffff',
              padding: '1.5rem 2rem',
              borderRadius: '12px 12px 0 0'
            }}>
              <h3 style={{
                margin: 0,
                fontSize: '1.5rem',
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}>
                {selectedRecord
                  ? `✏️ Edit ${attendanceType === 'regular' ? 'Attendance' : 'Training Attendance'} Record`
                  : `➕ Add ${attendanceType === 'regular' ? 'Attendance' : 'Training Attendance'} Record`
                }
              </h3>
              <p style={{ margin: '0.5rem 0 0 0', opacity: 0.9, fontSize: '0.95rem' }}>
                {selectedRecord
                  ? `Update ${attendanceType} attendance information`
                  : `Enter new ${attendanceType} attendance details`
                }
              </p>
            </div>

            {/* Modal Content */}
            <div style={{
              padding: '2rem',
              overflowY: 'auto',
              flex: 1
            }}>
              {attendanceType === 'regular' ? (
                // Regular Attendance Form
                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '1.5rem' }}>
                  {/* Date */}
                  <div>
                    <label style={{
                      display: 'block',
                      fontSize: '0.9rem',
                      fontWeight: 600,
                      color: '#374151',
                      marginBottom: '0.5rem'
                    }}>
                      Date *
                    </label>
                    <input
                      type="date"
                      value={formData.date}
                      onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: `2px solid ${formErrors.date ? '#dc2626' : '#e5e7eb'}`,
                        borderRadius: '8px',
                        fontSize: '1rem',
                        outline: 'none',
                        transition: 'border-color 0.3s ease',
                        backgroundColor: formErrors.date ? '#fef2f2' : '#ffffff'
                      }}
                    />
                    {formErrors.date && (
                      <p style={{ color: '#dc2626', fontSize: '0.8rem', margin: '0.25rem 0 0 0' }}>
                        {formErrors.date}
                      </p>
                    )}
                  </div>

                  {/* Student ID */}
                  <div>
                    <label style={{
                      display: 'block',
                      fontSize: '0.9rem',
                      fontWeight: 600,
                      color: '#374151',
                      marginBottom: '0.5rem'
                    }}>
                      Student ID *
                    </label>
                    <input
                      type="text"
                      value={formData.student_id}
                      onChange={(e) => setFormData({ ...formData, student_id: e.target.value })}
                      placeholder="e.g., 000-103-2022-014"
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: `2px solid ${formErrors.student_id ? '#dc2626' : '#e5e7eb'}`,
                        borderRadius: '8px',
                        fontSize: '1rem',
                        outline: 'none',
                        transition: 'border-color 0.3s ease',
                        backgroundColor: formErrors.student_id ? '#fef2f2' : '#ffffff'
                      }}
                    />
                    {resolvedStudentLoading && (
                      <p style={{ color: '#6b7280', fontSize: '0.85rem', margin: '0.4rem 0 0 0', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                        <span>🔍</span> Looking up student details...
                      </p>
                    )}
                    {!resolvedStudentLoading && resolvedStudent && (
                      <div style={{
                        marginTop: '0.4rem',
                        padding: '0.5rem 0.75rem',
                        background: '#f0fdf4',
                        border: '1px solid #bbf7d0',
                        borderRadius: '6px',
                        color: '#166534',
                        fontSize: '0.85rem',
                        lineHeight: '1.4'
                      }}>
                        <strong>✓ Student:</strong> {resolvedStudent.name}
                        <br />
                        <span style={{ fontSize: '0.75rem', color: '#15803d' }}>
                          Dept: {resolvedStudent.dept} | Year: {resolvedStudent.year} | Batch: {resolvedStudent.batch_name}
                        </span>
                      </div>
                    )}
                    {!resolvedStudentLoading && !resolvedStudent && (formData.student_id || '').trim().length >= 3 && (
                      <p style={{ color: '#b45309', fontSize: '0.85rem', margin: '0.4rem 0 0 0' }}>
                        ⚠️ Student details not found in database.
                      </p>
                    )}
                    {formErrors.student_id && (
                      <p style={{ color: '#dc2626', fontSize: '0.8rem', margin: '0.25rem 0 0 0' }}>
                        {formErrors.student_id}
                      </p>
                    )}
                  </div>

                  {/* Department */}
                  <div>
                    <label style={{
                      display: 'block',
                      fontSize: '0.9rem',
                      fontWeight: 600,
                      color: '#374151',
                      marginBottom: '0.5rem'
                    }}>
                      Department *
                    </label>
                    <select
                      value={formData.department}
                      onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                      disabled={departmentsLoading}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: `2px solid ${formErrors.department ? '#dc2626' : '#e5e7eb'}`,
                        borderRadius: '8px',
                        fontSize: '1rem',
                        outline: 'none',
                        transition: 'border-color 0.3s ease',
                        backgroundColor: formErrors.department ? '#fef2f2' : '#ffffff',
                        cursor: departmentsLoading ? 'not-allowed' : 'pointer'
                      }}
                    >
                      <option value="">
                        {departmentsLoading ? 'Loading departments...' : 'Select Department'}
                      </option>
                      {departments.map(dept => (
                        <option key={dept.value} value={dept.value}>{dept.label}</option>
                      ))}
                    </select>
                    {formErrors.department && (
                      <p style={{ color: '#dc2626', fontSize: '0.8rem', margin: '0.25rem 0 0 0' }}>
                        {formErrors.department}
                      </p>
                    )}
                  </div>

                  {/* Year */}
                  <div>
                    <label style={{
                      display: 'block',
                      fontSize: '0.9rem',
                      fontWeight: 600,
                      color: '#374151',
                      marginBottom: '0.5rem'
                    }}>
                      Year *
                    </label>
                    <select
                      value={formData.year}
                      onChange={(e) => setFormData({ ...formData, year: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: `2px solid ${formErrors.year ? '#dc2626' : '#e5e7eb'}`,
                        borderRadius: '8px',
                        fontSize: '1rem',
                        outline: 'none',
                        transition: 'border-color 0.3s ease',
                        backgroundColor: formErrors.year ? '#fef2f2' : '#ffffff'
                      }}
                    >
                      <option value="">Select Year</option>
                      {years.map(year => (
                        <option key={year} value={year}>Year {year}</option>
                      ))}
                      {!years.includes(formData.year) && formData.year && (
                        <option key={formData.year} value={formData.year}>Year {formData.year}</option>
                      )}
                    </select>
                    {formErrors.year && (
                      <p style={{ color: '#dc2626', fontSize: '0.8rem', margin: '0.25rem 0 0 0' }}>
                        {formErrors.year}
                      </p>
                    )}
                  </div>

                  {/* Batch Name */}
                  <div>
                    <label style={{
                      display: 'block',
                      fontSize: '0.9rem',
                      fontWeight: 600,
                      color: '#374151',
                      marginBottom: '0.5rem'
                    }}>
                      Batch Name *
                    </label>
                    <input
                      type="text"
                      value={formData.batch_name}
                      onChange={(e) => setFormData({ ...formData, batch_name: e.target.value })}
                      placeholder="e.g., Diploma CS-VI, BCA-2024-A"
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: `2px solid ${formErrors.batch_name ? '#dc2626' : '#e5e7eb'}`,
                        borderRadius: '8px',
                        fontSize: '1rem',
                        outline: 'none',
                        transition: 'border-color 0.3s ease',
                        backgroundColor: formErrors.batch_name ? '#fef2f2' : '#ffffff'
                      }}
                    />
                    {formErrors.batch_name && (
                      <p style={{ color: '#dc2626', fontSize: '0.8rem', margin: '0.25rem 0 0 0' }}>
                        {formErrors.batch_name}
                      </p>
                    )}
                  </div>

                  {/* Status */}
                  <div>
                    <label style={{
                      display: 'block',
                      fontSize: '0.9rem',
                      fontWeight: 600,
                      color: '#374151',
                      marginBottom: '0.5rem'
                    }}>
                      Attendance Status *
                    </label>
                    <select
                      value={formData.status}
                      onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: `2px solid ${formErrors.status ? '#dc2626' : '#e5e7eb'}`,
                        borderRadius: '8px',
                        fontSize: '1rem',
                        outline: 'none',
                        transition: 'border-color 0.3s ease',
                        backgroundColor: formErrors.status ? '#fef2f2' : '#ffffff'
                      }}
                    >
                      <option value="present">Present</option>
                      <option value="absent">Absent</option>
                    </select>
                    {formErrors.status && (
                      <p style={{ color: '#dc2626', fontSize: '0.8rem', margin: '0.25rem 0 0 0' }}>
                        {formErrors.status}
                      </p>
                    )}
                  </div>

                  {/* Subject */}
                  <div style={{ gridColumn: '1 / -1' }}>
                    <label style={{
                      display: 'block',
                      fontSize: '0.9rem',
                      fontWeight: 600,
                      color: '#374151',
                      marginBottom: '0.5rem'
                    }}>
                      Subject *
                    </label>
                    <input
                      type="text"
                      value={formData.subject}
                      onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                      placeholder="e.g., CSE101, Computer Programming, Database Management"
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: `2px solid ${formErrors.subject ? '#dc2626' : '#e5e7eb'}`,
                        borderRadius: '8px',
                        fontSize: '1rem',
                        outline: 'none',
                        transition: 'border-color 0.3s ease',
                        backgroundColor: formErrors.subject ? '#fef2f2' : '#ffffff'
                      }}
                    />
                    {formErrors.subject && (
                      <p style={{ color: '#dc2626', fontSize: '0.8rem', margin: '0.25rem 0 0 0' }}>
                        {formErrors.subject}
                      </p>
                    )}
                  </div>
                </div>
              ) : (
                // Training Attendance Form
                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '1.5rem' }}>
                  {/* Date */}
                  <div>
                    <label style={{
                      display: 'block',
                      fontSize: '0.9rem',
                      fontWeight: 600,
                      color: '#374151',
                      marginBottom: '0.5rem'
                    }}>
                      Date *
                    </label>
                    <input
                      type="date"
                      value={trainingFormData.date}
                      onChange={(e) => setTrainingFormData({ ...trainingFormData, date: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: `2px solid ${formErrors.date ? '#dc2626' : '#e5e7eb'}`,
                        borderRadius: '8px',
                        fontSize: '1rem',
                        outline: 'none',
                        transition: 'border-color 0.3s ease',
                        backgroundColor: formErrors.date ? '#fef2f2' : '#ffffff'
                      }}
                    />
                    {formErrors.date && (
                      <p style={{ color: '#dc2626', fontSize: '0.8rem', margin: '0.25rem 0 0 0' }}>
                        {formErrors.date}
                      </p>
                    )}
                  </div>

                  {/* Training Program */}
                  <div>
                    <label style={{
                      display: 'block',
                      fontSize: '0.9rem',
                      fontWeight: 600,
                      color: '#374151',
                      marginBottom: '0.5rem'
                    }}>
                      Training Program *
                    </label>
                    <select
                      value={trainingFormData.training_program_id}
                      onChange={(e) => {
                        setTrainingFormData({ ...trainingFormData, training_program_id: e.target.value, training_batch_id: '', subject_code: '' });
                      }}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: `2px solid ${formErrors.training_program_id ? '#dc2626' : '#e5e7eb'}`,
                        borderRadius: '8px',
                        fontSize: '1rem',
                        outline: 'none',
                        transition: 'border-color 0.3s ease',
                        backgroundColor: formErrors.training_program_id ? '#fef2f2' : '#ffffff'
                      }}
                    >
                      <option value="">Select Training Program</option>
                      {trainingPrograms.map(program => (
                        <option key={program.id} value={program.id}>{program.program_name}</option>
                      ))}
                    </select>
                    {formErrors.training_program_id && (
                      <p style={{ color: '#dc2626', fontSize: '0.8rem', margin: '0.25rem 0 0 0' }}>
                        {formErrors.training_program_id}
                      </p>
                    )}
                  </div>

                  {/* Training Batch */}
                  <div>
                    <label style={{
                      display: 'block',
                      fontSize: '0.9rem',
                      fontWeight: 600,
                      color: '#374151',
                      marginBottom: '0.5rem'
                    }}>
                      Training Batch *
                    </label>
                    <select
                      value={trainingFormData.training_batch_id}
                      onChange={(e) => setTrainingFormData({ ...trainingFormData, training_batch_id: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: `2px solid ${formErrors.training_batch_id ? '#dc2626' : '#e5e7eb'}`,
                        borderRadius: '8px',
                        fontSize: '1rem',
                        outline: 'none',
                        transition: 'border-color 0.3s ease',
                        backgroundColor: formErrors.training_batch_id ? '#fef2f2' : '#ffffff'
                      }}
                    >
                      <option value="">Select Training Batch</option>
                      {trainingBatches.map(batch => (
                        <option key={batch.id} value={batch.id}>{batch.batch_name}</option>
                      ))}
                    </select>
                    {formErrors.training_batch_id && (
                      <p style={{ color: '#dc2626', fontSize: '0.8rem', margin: '0.25rem 0 0 0' }}>
                        {formErrors.training_batch_id}
                      </p>
                    )}
                  </div>

                  {/* Student ID */}
                  <div>
                    <label style={{
                      display: 'block',
                      fontSize: '0.9rem',
                      fontWeight: 600,
                      color: '#374151',
                      marginBottom: '0.5rem'
                    }}>
                      Student ID *
                    </label>
                    <input
                      type="text"
                      value={trainingFormData.student_id}
                      onChange={(e) => setTrainingFormData({ ...trainingFormData, student_id: e.target.value })}
                      placeholder="e.g., 002-103-2024-001"
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: `2px solid ${formErrors.student_id ? '#dc2626' : '#e5e7eb'}`,
                        borderRadius: '8px',
                        fontSize: '1rem',
                        outline: 'none',
                        transition: 'border-color 0.3s ease',
                        backgroundColor: formErrors.student_id ? '#fef2f2' : '#ffffff'
                      }}
                    />
                    {resolvedStudentLoading && (
                      <p style={{ color: '#6b7280', fontSize: '0.85rem', margin: '0.4rem 0 0 0', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                        <span>🔍</span> Looking up student details...
                      </p>
                    )}
                    {!resolvedStudentLoading && resolvedStudent && (
                      <div style={{
                        marginTop: '0.4rem',
                        padding: '0.5rem 0.75rem',
                        background: '#f0fdf4',
                        border: '1px solid #bbf7d0',
                        borderRadius: '6px',
                        color: '#166534',
                        fontSize: '0.85rem',
                        lineHeight: '1.4'
                      }}>
                        <strong>✓ Student:</strong> {resolvedStudent.name}
                        <br />
                        <span style={{ fontSize: '0.75rem', color: '#15803d' }}>
                          Dept: {resolvedStudent.dept} | Year: {resolvedStudent.year} | Batch: {resolvedStudent.batch_name}
                        </span>
                      </div>
                    )}
                    {!resolvedStudentLoading && !resolvedStudent && (trainingFormData.student_id || '').trim().length >= 3 && (
                      <p style={{ color: '#b45309', fontSize: '0.85rem', margin: '0.4rem 0 0 0' }}>
                        ⚠️ Student details not found in database.
                      </p>
                    )}
                    {formErrors.student_id && (
                      <p style={{ color: '#dc2626', fontSize: '0.8rem', margin: '0.25rem 0 0 0' }}>
                        {formErrors.student_id}
                      </p>
                    )}
                  </div>

                  {/* Subject */}
                  <div>
                    <label style={{
                      display: 'block',
                      fontSize: '0.9rem',
                      fontWeight: 600,
                      color: '#374151',
                      marginBottom: '0.5rem'
                    }}>
                      Subject *
                    </label>
                    <select
                      value={trainingFormData.subject_code}
                      onChange={(e) => setTrainingFormData({ ...trainingFormData, subject_code: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: `2px solid ${formErrors.subject_code ? '#dc2626' : '#e5e7eb'}`,
                        borderRadius: '8px',
                        fontSize: '1rem',
                        outline: 'none',
                        transition: 'border-color 0.3s ease',
                        backgroundColor: formErrors.subject_code ? '#fef2f2' : '#ffffff'
                      }}
                    >
                      <option value="">Select Subject</option>
                      {trainingSubjects.map(subject => (
                        <option key={subject.id} value={subject.subject_code}>
                          {subject.subject_code} - {subject.subject_name}
                        </option>
                      ))}
                    </select>
                    {formErrors.subject_code && (
                      <p style={{ color: '#dc2626', fontSize: '0.8rem', margin: '0.25rem 0 0 0' }}>
                        {formErrors.subject_code}
                      </p>
                    )}
                  </div>

                  {/* Status */}
                  <div>
                    <label style={{
                      display: 'block',
                      fontSize: '0.9rem',
                      fontWeight: 600,
                      color: '#374151',
                      marginBottom: '0.5rem'
                    }}>
                      Attendance Status *
                    </label>
                    <select
                      value={trainingFormData.status}
                      onChange={(e) => setTrainingFormData({ ...trainingFormData, status: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: `2px solid ${formErrors.status ? '#dc2626' : '#e5e7eb'}`,
                        borderRadius: '8px',
                        fontSize: '1rem',
                        outline: 'none',
                        transition: 'border-color 0.3s ease',
                        backgroundColor: formErrors.status ? '#fef2f2' : '#ffffff'
                      }}
                    >
                      <option value="present">Present</option>
                      <option value="absent">Absent</option>
                    </select>
                    {formErrors.status && (
                      <p style={{ color: '#dc2626', fontSize: '0.8rem', margin: '0.25rem 0 0 0' }}>
                        {formErrors.status}
                      </p>
                    )}
                  </div>

                  {/* Session Hours */}
                  <div>
                    <label style={{
                      display: 'block',
                      fontSize: '0.9rem',
                      fontWeight: 600,
                      color: '#374151',
                      marginBottom: '0.5rem'
                    }}>
                      Session Hours
                    </label>
                    <input
                      type="number"
                      min="0.5"
                      max="8"
                      step="0.5"
                      value={trainingFormData.session_hours}
                      onChange={(e) => setTrainingFormData({ ...trainingFormData, session_hours: parseFloat(e.target.value) || 1.0 })}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '2px solid #e5e7eb',
                        borderRadius: '8px',
                        fontSize: '1rem',
                        outline: 'none',
                        transition: 'border-color 0.3s ease',
                        backgroundColor: '#ffffff'
                      }}
                      onFocus={(e) => e.target.style.borderColor = '#238548'}
                      onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                    />
                  </div>

                  {/* Remarks */}
                  <div style={{ gridColumn: '1 / -1' }}>
                    <label style={{
                      display: 'block',
                      fontSize: '0.9rem',
                      fontWeight: 600,
                      color: '#374151',
                      marginBottom: '0.5rem'
                    }}>
                      Remarks (Optional)
                    </label>
                    <textarea
                      value={trainingFormData.remarks}
                      onChange={(e) => setTrainingFormData({ ...trainingFormData, remarks: e.target.value })}
                      placeholder="Additional notes or comments about the training session..."
                      rows="3"
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '2px solid #e5e7eb',
                        borderRadius: '8px',
                        fontSize: '1rem',
                        outline: 'none',
                        transition: 'border-color 0.3s ease',
                        backgroundColor: '#ffffff',
                        resize: 'vertical'
                      }}
                      onFocus={(e) => e.target.style.borderColor = '#238548'}
                      onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div style={{
              padding: '1.5rem 2rem',
              borderTop: '1px solid #f1f5f9',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '1rem',
              backgroundColor: '#f8fafc'
            }}>
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setShowEditModal(false);
                  resetForm();
                }}
                style={{
                  padding: '0.75rem 1.5rem',
                  border: '2px solid #e5e7eb',
                  backgroundColor: '#ffffff',
                  color: '#374151',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  fontWeight: 500
                }}
                onMouseEnter={(e) => {
                  e.target.style.borderColor = '#d1d5db';
                  e.target.style.backgroundColor = '#f9fafb';
                }}
                onMouseLeave={(e) => {
                  e.target.style.borderColor = '#e5e7eb';
                  e.target.style.backgroundColor = '#ffffff';
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={submitting}
                style={{
                  padding: '0.75rem 2rem',
                  border: 'none',
                  background: submitting
                    ? 'linear-gradient(135deg, #9ca3af 0%, #d1d5db 100%)'
                    : 'linear-gradient(135deg, #238548 0%, #97d6ac 100%)',
                  color: '#ffffff',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  cursor: submitting ? 'not-allowed' : 'pointer',
                  transition: 'all 0.3s ease',
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
                onMouseEnter={(e) => {
                  if (!submitting) {
                    e.target.style.transform = 'translateY(-2px)';
                    e.target.style.boxShadow = '0 8px 25px rgba(35, 133, 72, 0.3)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!submitting) {
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = 'none';
                  }
                }}
              >
                {submitting ? (
                  <>
                    <div style={{
                      width: '16px',
                      height: '16px',
                      border: '2px solid #ffffff',
                      borderTop: '2px solid transparent',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }}></div>
                    Saving...
                  </>
                ) : (
                  <>
                    {selectedRecord
                      ? `💾 Update ${attendanceType === 'regular' ? 'Record' : 'Training Record'}`
                      : `➕ Add ${attendanceType === 'regular' ? 'Record' : 'Training Record'}`
                    }
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedRecord && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000,
          padding: '1rem'
        }}>
          <div style={{
            background: '#ffffff',
            borderRadius: '12px',
            boxShadow: '0 25px 50px rgba(0, 0, 0, 0.25)',
            maxWidth: '400px',
            width: '100%'
          }}>
            {/* Modal Header */}
            <div style={{
              padding: '2rem 2rem 1rem 2rem',
              textAlign: 'center'
            }}>
              <div style={{
                width: '64px',
                height: '64px',
                background: '#fee2e2',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 1rem',
                fontSize: '2rem'
              }}>
                🗑️
              </div>
              <h3 style={{
                margin: '0 0 1rem 0',
                fontSize: '1.5rem',
                fontWeight: 600,
                color: '#dc2626'
              }}>
                Delete {attendanceType === 'regular' ? 'Attendance' : 'Training Attendance'} Record
              </h3>
              <p style={{
                margin: '0 0 1rem 0',
                color: '#6b7280',
                lineHeight: 1.6
              }}>
                Are you sure you want to delete this {attendanceType} attendance record for <strong style={{ color: '#374151' }}>{selectedRecord.student_id}</strong>{attendanceType === 'training' && selectedRecord.program_name && <> from <strong style={{ color: '#374151' }}>{selectedRecord.program_name}</strong></>}?
              </p>
              {attendanceType === 'training' && (
                <div style={{
                  backgroundColor: '#f3f4f6',
                  padding: '1rem',
                  borderRadius: '8px',
                  margin: '1rem 0',
                  fontSize: '0.9rem'
                }}>
                  <div><strong>Date:</strong> {formatDate(selectedRecord.date)}</div>
                  <div><strong>Program:</strong> {selectedRecord.program_name}</div>
                  <div><strong>Subject:</strong> {selectedRecord.subject_name}</div>
                  <div><strong>Status:</strong> {selectedRecord.status}</div>
                </div>
              )}
              <p style={{
                margin: 0,
                color: '#dc2626',
                fontSize: '0.9rem',
                fontWeight: 500
              }}>
                This action cannot be undone.
              </p>
            </div>

            {/* Modal Footer */}
            <div style={{
              padding: '1rem 2rem 2rem 2rem',
              display: 'flex',
              justifyContent: 'center',
              gap: '1rem'
            }}>
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setSelectedRecord(null);
                }}
                style={{
                  padding: '0.75rem 1.5rem',
                  border: '2px solid #e5e7eb',
                  backgroundColor: '#ffffff',
                  color: '#374151',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  fontWeight: 500
                }}
                onMouseEnter={(e) => {
                  e.target.style.borderColor = '#d1d5db';
                  e.target.style.backgroundColor = '#f9fafb';
                }}
                onMouseLeave={(e) => {
                  e.target.style.borderColor = '#e5e7eb';
                  e.target.style.backgroundColor = '#ffffff';
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={submitting}
                style={{
                  padding: '0.75rem 1.5rem',
                  border: 'none',
                  background: submitting
                    ? 'linear-gradient(135deg, #9ca3af 0%, #d1d5db 100%)'
                    : 'linear-gradient(135deg, #dc2626 0%, #f87171 100%)',
                  color: '#ffffff',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  cursor: submitting ? 'not-allowed' : 'pointer',
                  transition: 'all 0.3s ease',
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
                onMouseEnter={(e) => {
                  if (!submitting) {
                    e.target.style.transform = 'translateY(-2px)';
                    e.target.style.boxShadow = '0 8px 25px rgba(220, 38, 38, 0.3)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!submitting) {
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = 'none';
                  }
                }}
              >
                {submitting ? (
                  <>
                    <div style={{
                      width: '16px',
                      height: '16px',
                      border: '2px solid #ffffff',
                      borderTop: '2px solid transparent',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }}></div>
                    Deleting...
                  </>
                ) : (
                  `🗑️ Delete ${attendanceType === 'regular' ? 'Record' : 'Training Record'}`
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default AdminAttendanceManagement;